import React, { createContext, useContext, useState, useEffect } from 'react';

// ─── Dictionary ──────────────────────────────────────────────
const translations = {
    en: {
        // General
        cancel: 'Cancel',
        close: 'Close',
        loading: 'Loading…',
        search: 'Search by name or email…',
        business_not_found: 'Business not found.',
        client_label: 'Client',
        visits_label: 'Visits',
        stamp_n: 'Stamp {{count}}',
        err_biz_loading: 'Wait a moment... Still fetching your business details.',
        hello_prefix: 'Hello',
        now: 'Now',
        preview: 'Preview',
        apple: 'Apple',
        google: 'Google',
        name: 'Name',
        until_reward: 'UNTIL REWARD',
        stamps_suffix: 'stamps',
        ready: 'Ready!',
        email: 'Email',
        logo: 'Logo',
        design: 'Design',

        // Navbar
        my_qr_code: '🏪 My QR Code',
        scan_qr: '📷 Scan QR',
        add_client: '➕ Add Client',
        sign_out: 'Sign Out',
        dashboard: 'Dashboard',
        back: 'Back',
        card_design: 'Stamp Card Design',

        // Landing
        landing_subtitle: 'The beautiful, digital loyalty card system. Keep your customers coming back without the paper cards.',
        get_started_free: 'Get Started Free',
        see_how_it_works: 'See How It Works',
        how_it_works: 'How it works',
        step_1_title: '1. Sign up your business',
        step_1_desc: "Create an account in seconds. You'll get a unique QR code for your shop counter.",
        step_2_title: '2. Clients scan to join',
        step_2_desc: 'No apps required. Customers scan your QR to instantly register their digital card.',
        step_3_title: '3. Scan to reward',
        step_3_desc: 'When they visit, scan their phone to add a stamp. 10 stamps = free reward!',
        features_title: 'Why choose our platform?',
        feat_1_title: 'No App Required',
        feat_1_desc: 'Operates entirely in the browser. Customers just scan and save to Google Wallet or Apple Wallet.',
        feat_2_title: 'Instant Setup',
        feat_2_desc: 'Be up and running in minutes. No complex hardware or POS integration required.',
        feat_3_title: 'Data & Analytics',
        feat_3_desc: 'Know exactly who your best customers are and how often they visit your business.',
        ready_to_start: 'Ready to modernize your loyalty program?',
        create_business_account: 'Create Business Account',
        go_to_dashboard: 'Go to Dashboard',
        landing_badge: '✨ The #1 Digital Stamp Card',
        hero_title_1: 'Old-school loyalty,',
        hero_title_2: 'reimagined for the digital age.',
        customer_rating: 'Customer Rating',
        apps_to_download: 'Apps to Download',
        data_ownership: 'Data Ownership',
        how_it_works_subtitle: 'Simple to use. Easy to set up. Running your loyalty program has never been this effortless.',
        footer_tagline: 'Empowering businesses with modern, digital loyalty solutions.',
        product: 'Product',
        features: 'Features',
        company: 'Company',
        about_us: 'About Us',
        contact: 'Contact',
        all_rights_reserved: 'All rights reserved.',

        // Redesign Sections
        announcement_text: '🏆 The #1 Digital Stamp Card — No apps. No paper. Just results.',
        promo_banner: '🎁 Get 1 month free trial! Offer ends December 1st',
        see_more: 'See more →',
        pill_badge: '✦ Digital cards for your business',
        hero_h1: 'Every customer who doesn\'t return is money you lose.',
        hero_sub: 'Create digital stamp cards in minutes. Your customers save them to Apple or Google Wallet — without downloading any app.',
        cta_free: 'Get a free tour in the app',
        cta_demo: 'See plans',
        demo_banner_title: 'Demo Mode: Wafiyo Cafe',
        demo_banner_text: 'Explore the dashboard with mock data. Changes will not be saved.',
        install_banner_title: 'Install Wafiyo',
        install_banner_android: 'Add Wafiyo to your home screen for quick access.',
        install_banner_ios: 'Tap the Share button, then "Add to Home Screen".',
        install_banner_btn: 'Install',
        cta_real_signup: 'Start Real Trial →',
        demo_feature_restricted: 'Wallet integration is disabled in demo mode.',
        demo_sidebar_cta: 'Ready to start your real loyalty program?',
        trust_1: 'Free to start',
        trust_2: '30-day free trial',
        trust_3: 'Setup in 5 min',
        social_proof: 'Trusted by businesses across the world',
        problem_h2: 'Paper cards are broken',
        pain_1_title: 'Lost or forgotten at home',
        pain_1_desc: 'Customers forget them or lose them, making the loyalty loop break easily.',
        pain_2_title: "You don't know your best customers",
        pain_2_desc: "It's impossible to track who visits most without digital tools.",
        pain_3_title: "Customers don't return after the first visit",
        pain_3_desc: 'Without a reason to come back, one-time customers disappear.',
        transition_text: 'Wafiyo solves all this — in less than 5 minutes.',
        how_it_works_h2: 'How it works',
        step_1_title: '1. Register your business',
        step_1_desc: 'Create your account and get your unique QR code.',
        step_2_title: '2. Your customers scan',
        step_2_desc: 'They join instantly from their browser.',
        step_3_title: '3. Scan and reward',
        step_3_desc: 'You scan their card on every visit.',
        feat_1_title_new: 'No Applications',
        feat_1_desc_new: 'Your customers only need their phone. They scan, register, and save their card to Wallet in seconds.',
        feat_2_title_new: 'Design your card',
        feat_2_desc_new: 'Customize colors, logo, and stamp icons to reflect your brand.',
        feat_3_title_new: 'Analytics Dashboard',
        feat_3_desc_new: 'A full analytics dashboard with bar charts, area charts, and donut charts. Track visits by day, hour, and month. See your retention rate and conversion metrics at a glance.',
        feat_4_title_new: 'Smart Insights',
        feat_4_desc_new: 'Know your best day of the week, your peak hours, and which clients are one stamp away from their reward. Data-driven decisions made simple.',
        metric_1_val: '40%',
        metric_1_label: 'Average conversion rate',
        metric_2_val: '0 apps',
        metric_2_label: 'Downloaded by your customers',
        metric_3_val: '5 min',
        metric_3_label: 'To set up your program',
        testi_h2: 'What our businesses are saying',
        pricing_toggle_mo: 'Monthly',
        pricing_toggle_yr: 'Yearly',
        popular_badge: 'Most Popular',
        cancel_anytime: '✓ Free trial included. No commitment. Cancel anytime.',
        cta_monthly: 'Start your free trial',
        cta_yearly: 'Save with the yearly plan',
        // Analytics page
        analytics_visits_this_week: 'Visits This Week',
        analytics_retention_rate: 'Retention Rate',
        analytics_stamps_given: 'Stamps Given',
        analytics_conversion_rate: 'Conversion Rate',
        analytics_visits_per_day: 'Visits per day',
        analytics_tab_week: 'Week',
        analytics_tab_month: 'Month',
        analytics_tab_year: 'Year',
        analytics_visits_per_hour: 'Visits per hour',
        analytics_client_retention: 'Client Retention',
        analytics_returning: 'Returning',
        analytics_new: 'New',
        analytics_top_clients: 'Most Active Clients',
        analytics_visits: 'visits',
        analytics_stamps: 'stamps',
        analytics_no_clients: 'No clients yet',
        analytics_best_day: 'Best Day',
        analytics_peak_hour: 'Peak Hour',
        analytics_upcoming_rewards: 'Upcoming Rewards',
        analytics_total_visits: 'total visits',
        analytics_clients: 'clients',
        analytics_one_stamp_away: '1 stamp away from reward',
        last_visit: 'Last visit',
        no_visits_yet: 'No visits yet',
        time_ago: 'ago',
        time_just_now: 'Just now',
        notif_title: 'Send Notification',
        notif_placeholder: 'Type your message...',
        notif_send: 'Send to',
        notif_clients_suffix: 'clients',
        notif_sending: 'Sending...',
        notif_cooldown: 'Next notification available in',
        notif_char_limit: 'characters',
        notif_last_sent: 'Last sent',
        notif_success: 'Notification sent successfully!',
        notif_empty: 'Please type a message',
        notif_no_clients: 'No clients to notify',
        notif_remaining: 'remaining today',
        cookie_text: 'We use cookies to improve your experience. You can accept or decline non-essential cookies.',
        cookie_policy_link: 'Privacy Policy',
        cookie_accept: 'Accept',
        cookie_decline: 'Decline',
        analytics_title: 'Analytics',
        view_all: 'View All',
        sp_cafes: 'Coffee Shops',
        sp_barbers: 'Barbershops',
        sp_restaurants: 'Restaurants',
        sp_shops: 'Shops',
        salon_social_proof: 'Trusted by top beauty professionals worldwide',
        salon_sp_1: 'Hair Salons',
        salon_sp_2: 'Nail Studios',
        salon_sp_3: 'Aesthetics',
        salon_sp_4: 'Spas & Wellness',
        mock_best_day: 'Best Day',
        mock_saturday: 'Saturday',
        mock_peak_hour: 'Peak Hour',
        mock_upcoming_rewards: 'Upcoming Rewards',
        mock_clients_count: '3 clients',
        feat_5_title_new: 'Push Notifications via Wallet',
        feat_5_desc_new: 'Send custom messages directly to your clients\' phones. Your message appears on their lock screen through Apple Wallet and Google Wallet — no app needed.',
        feat_5_mock_msg: 'Happy hour today! 🔥',
        feat_5_mock_sent: 'Sent to 47 clients ✓',
        feat_5_mock_lock: 'Appears on lock screen',
        push_notifications_feature: 'Push Notifications',
        hero_push_bold: 'You\'re 1 visit away!',
        hero_push_body: 'Come this week and get your next coffee free at Tormen Cafetería.',
        hero_push_meta: 'Push notification · 2 hours ago',
        hero_social_proof: '+200 businesses',
        hero_social_proof_suffix: 'already use Wafiyo worldwide',
        final_cta_h2: 'Ready to modernize your loyalty program?',
        final_cta_sub: 'Join the businesses already building loyalty without paper.',
        final_cta_btn: 'Start Free Now',
        final_cta_micro: '30-day free trial · Setup in 5 minutes',
        salon_hero_h1: 'Every empty chair is lost revenue.',
        salon_hero_sub: 'Fill your salon by sending push notifications directly to your clients\' phones. Digital stamp cards for Google & Apple Wallet. No apps.',
        salon_hero_push_bold: 'We have an opening at 5 PM!',
        salon_hero_push_body: 'Book now and get double stamps today at Wafiyo Beauty.',
        salon_pain_1_title: 'Paper cards are forgotten',
        salon_pain_1_desc: 'Clients forget their loyalty cards, breaking the habit of returning to your salon.',
        salon_pain_2_title: 'Unfilled cancellations',
        salon_pain_2_desc: 'When a client cancels last minute, you have no direct way to alert others and fill the slot.',
        salon_pain_3_title: 'No connection between appointments',
        salon_pain_3_desc: 'Without a reason or reminder to come back, casual clients never become regulars.',
        salon_feat_5_mock_msg: 'Flash promo! 20% off manicures today.',
        salon_hero_social_proof: '+150 beauty salons',
        salon_testi_1_quote: 'Wafiyo helps me fill last-minute cancellations with a single push notification to my clients. It\'s incredible.',
        salon_testi_1_type: 'Beauty & Hair Salon',
        salon_testi_2_quote: 'Since switching to digital loyalty cards, my clients never forget them. We\'ve seen a 30% increase in return visits for manicures.',
        salon_testi_2_type: 'Nail Studio & Spa',
        salon_testi_3_quote: 'The ROI is amazing. A quick push notification about our Thursday promo fills up our schedule every single week.',
        salon_testi_3_type: 'Aesthetic Clinic',
        salon_urgency_banner: 'Special Offer: 30 Days Free (Only 12 spots left)',

        salon_feat_1_title: 'Fill empty slots instantly',
        salon_feat_1_desc: 'Send a push notification directly to your clients\' phones when you have a last-minute cancellation. Turn dead hours into booked appointments.',
        salon_feat_2_title: 'Cards that never get lost',
        salon_feat_2_desc: 'Your clients will always have your loyalty card in their Apple or Google Wallet. No more forgotten paper cards at home.',
        salon_feat_3_title: 'Know your best clients',
        salon_feat_3_desc: 'Discover who visits most often and which days are your slowest. Reward their loyalty and keep them coming back.',
        salon_feat_4_title: 'Your salon, your brand',
        salon_feat_4_desc: 'Design the card with your salon\'s logo and colors. Create a professional, elegant experience that stands out.',

        // Login
        back_to_home: 'Back to Home',
        loyalty_card_manager: 'Wafiyo',
        sign_in_business: 'Sign in to your business account',
        create_biz_account: 'Create your business account',
        business_name: 'Business Name',
        password: 'Password',
        please_wait: 'Please wait…',
        sign_in: 'Sign In',
        create_account: 'Create Account',
        dont_have_account: "Don't have an account? ",
        already_have_account: 'Already have an account? ',
        sign_up_link: 'Sign Up',
        forgot_password_link: 'Forgot password?',
        forgot_pw_title: 'Reset Password',
        forgot_pw_subtitle: 'Enter your email and we will send you a link',
        forgot_pw_success: 'Check your email, we have sent you a link to reset your password',
        send_link: 'Send Link',
        err_email_not_found: 'We could not find an account with this email',
        reset_pw_title: 'New Password',
        reset_pw_subtitle: 'Enter your new password',
        reset_pw_label: 'New password',
        reset_pw_confirm: 'Confirm password',
        reset_pw_update_btn: 'Update password',
        err_pw_match: 'Passwords do not match',
        err_pw_length: 'Password must be at least 8 characters long',
        reset_pw_success: 'Password successfully updated',
        redirecting_login: 'Redirecting to login...',
        verifying: 'Verifying...',
        account_confirmed: 'Account confirmed!',
        account_confirmed_msg: 'Your account has been successfully verified',
        invalid_link: 'Invalid link',
        invalid_link_msg: 'This link has expired or was already used. Try registering again or contact support.',
        go_to_dashboard: 'Go to dashboard',
        back_to_login: '← Back to login',
        check_email_in: 'Check your email at',
        check_email_msg: 'We have sent you a confirmation link to activate your account.',
        check_spam: "Can't find it? Check your spam folder",
        i_confirmed_already: 'I already confirmed my email',
        err_confirm_email_first: 'Confirm your email before continuing',
        back_to_register: 'Back to sign up',

        // Pricing
        choose_your_plan: 'Choose Your Plan',
        pricing_subtitle: 'Unlock the full potential of your business with our professional loyalty system.',
        monthly_plan: 'Monthly Plan',
        yearly_plan: 'Yearly Plan',
        month: 'month',
        first_month: 'first month',
        then_price: 'Then {{symbol}}{{amount}}/month',
        year: 'year',
        days_free_trial: 'days free trial',
        migration_pricing_title: 'Payment provider update',
        migration_pricing_desc: 'We\'ve switched to a new payment provider. Please select a plan below to continue using Wafiyo. Your free trial has already been used, so billing will start immediately.',
        subscribe_now: 'Subscribe Now',
        choose_monthly: 'Choose Monthly Plan',
        choose_yearly: 'Choose Yearly Plan',
        migration_alert_expired_title: '⚠️ Action required',
        migration_alert_update_title: '💳 Update your payment method',
        migration_alert_expired_desc: 'Your subscription has ended. Reactivate your {{plan}} plan to continue managing your loyalty cards and clients.',
        migration_alert_update_desc_date: 'We\'ve switched to a new payment provider. Your {{plan}} plan is active until {{date}} — just add your card now and you won\'t be charged until then. It only takes a minute!',
        migration_alert_update_desc_nodate: 'We\'ve switched to a new payment provider. Please add your payment details to keep your {{plan}} plan active.',
        migration_btn_reactivate: 'Reactivate {{plan}} plan',
        migration_btn_secure: 'Secure my {{plan}} plan',
        activating: 'Activating...',

        // ROI Calculator
        roi_section_title: 'See the impact on your business',
        roi_title: 'Calculate your ROI',
        roi_desc: 'See how much revenue you are missing out on by not building customer loyalty.',
        roi_daily_customers: 'Daily Customers',
        roi_avg_spend: 'Average Ticket',
        roi_extra_revenue: 'Potential Extra Revenue',
        roi_per_month: 'per month',
        roi_disclaimer: 'Based on a conservative 12% revenue increase from improved retention and return rates.',
        roi_cta: 'Start Recovering Revenue',

        unlimited_clients: 'Unlimited Clients',
        custom_card_designs: 'Custom Card Designs',
        qr_code_scanning: 'QR Code Scanning',
        testi_1_quote: "Wafiyo has simplified my loyalty program. My clients love not having to keep another piece of paper in their wallet.",
        testi_1_type: "Beauty Salon",
        testi_2_quote: "The integration with Apple and Google Wallet is what sets us apart. It's modern, fast, and professional.",
        testi_2_type: "Coffee Shop",
        testi_3_quote: "Since we eliminated physical cards, my customer return rate has gone up by 20%. Incredible.",
        testi_3_type: "Restaurant",
        google_wallet_integration: 'Google & Apple Wallet Integration',
        all_monthly_features: 'All Monthly features',
        three_months_free: 'Includes 3 months free',
        priority_support: 'Priority Support',
        best_value: 'Best Value',
        save_amount: 'Save {{symbol}}{{amount}} per year!',
        start_free_trial: 'Start 30-day Free Trial',
        free_trial_badge: '1 Month Free Trial',
        choose_yearly: 'Choose Yearly Plan',
        pricing_footer_text: 'All plans include unlimited stamps, scan history, and basic analytics.',

        // Dashboard
        total_clients: 'Total Clients',
        total_visits: 'Total Visits',
        rewards_due: 'Rewards Due',
        clients: 'Clients',
        no_clients_yet: 'No clients yet — click "Add Client" to get started!',
        no_clients_match: 'No clients match your search.',
        loading_clients: 'Loading clients…',
        visits_today: 'Total Visits',
        rewards: 'Rewards',
        conversion: 'Conversion',
        conversion_tooltip: 'Calculation: (Total Stamps / Total Visits) * 100. It shows how many visitors convert into active stamps.',
        clients_tooltip: 'Total number of loyalty cards distributed to your clients.',
        visits_tooltip: 'Total number of stamps or visits recorded across all clients.',
        rewards_tooltip: 'Number of clients who have earned enough stamps and are ready to claim their free reward.',
        recent_activity: 'Recent Activity',
        no_recent_activity: 'No recent activity',
        no_clients_found: 'No clients found',
        settings: 'Settings',
        business_account: 'Business Account',
        user_email: 'User Email',
        subscription: 'Subscription',
        plan_status: 'Plan Status',
        active: 'Active',
        inactive: 'Inactive',
        manage_subscription: 'Manage Subscription',
        activity_visit: 'Visit recorded',
        activity_reg: 'New client joined',
        no_clients_to_export: 'No clients to export',
        csv_export_success: 'CSV exported successfully',
        download_csv: 'Download CSV',

        // Client Card
        progress_toward_reward: 'Progress toward free reward',
        visits: 'visits',
        reward_due: '🎉 Reward Due',
        record_visit: 'Record Visit',
        gift: '🎁 Gift',
        remove_visit: 'Remove',
        remove_visit_confirm: 'Are you sure you want to remove 1 visit from',
        hide_qr: 'Hide QR',
        show_qr: '🔲 Show QR',
        wallet_link: '📱 Wallet Link',
        client_scans_this: 'Client scans this to pull up their card',
        no_contact_info: 'No contact info',

        // Add Client Modal
        add_new_client: '➕ Add New Client',
        card_created: '✅ Card Created',
        client_name: 'Client Name *',
        optional: '(Optional)',
        phone: 'Phone',
        visits_for_free_reward: 'Visits for free reward',
        create_card: 'Create Card',
        creating: 'Creating…',
        share_with: 'Share this QR code or Google Wallet link with',
        client_registration_qr: 'Client Registration QR',
        add_to_wallet: 'Add to Google Wallet',
        add_to_apple_wallet: 'Add to Apple Wallet',
        wallet_not_configured: 'Google Wallet not configured — set up service account credentials to enable Wallet links.',

        // Card Customizer
        customize_card: 'Personalize Your Card',
        card_title: 'Card Title / Business Name',
        logo_upload: 'Business Logo',
        logo_change: 'Change logo',
        logo_remove: 'Remove',
        logo_upload_hint: 'PNG, JPG or WEBP — max 2MB',
        err_logo_too_large: 'File cannot exceed 2MB',
        err_logo_wrong_type: 'Only PNG, JPG or WEBP images are allowed',
        err_logo_upload_failed: 'Error uploading logo, please try again',
        color_preset: 'Branding Presets',
        background_color: 'Card Color',
        text_color: 'Text Color',
        number_color: 'Number Color',
        stamp_circle_color: 'Stamp Color',
        stamp_icon: 'Stamp Icon',
        stamp_goal: 'Stamps to Reward',
        preview_stamps: 'Preview Visit Count',
        reward_text: 'Reward (e.g. Free Haircut)',
        save_design: '💾 Publish Design',
        saving: 'Publishing...',
        live_preview: 'Live Preview',
        view_png: 'View as PNG →',
        wallet_preview: 'Mobile Wallet Preview',
        wallet_header: 'Choose your wallet header',
        design_saved_notify: 'Success! Your new stamp card design is now live.',

        // Business QR Modal
        business_signup_qr: '🏪 Business Sign-Up QR',
        print_or_display: 'Print or display this QR in your shop. Clients scan it to create their own loyalty card instantly — no app needed.',
        generating_qr: 'Generating QR…',
        download: '⬇️ Download',
        print: '🖨️ Print',
        print_poster_badge: 'Loyalty Terminal',
        print_poster_subtitle: 'Scan to join our digital loyalty programme and start earning rewards today.',
        print_poster_step_1: 'Scan QR',
        print_poster_step_2: 'Register',
        print_poster_step_3: 'Add to Wallet',
        print_poster_no_app: 'No app download needed.',
        print_poster_powered_by: 'Powered by Wafiyo',

        // Client Register
        join_loyalty: "Join Wafiyo — it's free!",
        full_name: 'Full Name',
        youre_in: "You're in!",
        loyalty_card_ready: 'Your loyalty card for {{businessName}} is ready.',
        show_qr_every_visit: 'Show this QR code to the team on every visit',
        your_goal: 'Your goal',
        collect_n_visits: 'Collect {{threshold}} visits to earn a free reward 🏆',
        welcome_aboard: 'Welcome aboard, {{clientName}}!',
        add_to_wallet_desc: 'Save your digital membership card to your phone for quick access.',

        // Notifications & Validation
        err_valid_name: 'Please enter a valid name (2-50 characters).',
        err_valid_email: "Oops! That email doesn't look quite right. Please double-check it.",
        err_password_min: 'Password must be at least 6 characters long.',
        err_password_weak: 'Almost there! To make your password stronger, please include at least one number.',
        err_valid_phone: 'Please enter a valid phone number.',
        err_threshold_range: 'Reward threshold must be between 1 and 100.',
        notify_reg_success: 'Registration successful! Please check your email.',
        notify_welcome_back: 'Welcome back!',
        notify_welcome_ready: 'Welcome! Your card is ready.',
        notify_visit_recorded: 'Visit recorded',
        notify_visit_removed: 'Visit removed',
        notify_reward_redeemed: '🎁 Reward redeemed!',
        err_not_confirmed: 'Email still unconfirmed. Please click the link in your inbox.',
        confirm_remove_title: 'Remove Visit',
        confirm_action: 'Confirm',

        // QR Scanner
        scan_title: '📷 Scan Client QR Code',
        point_camera: "Point camera at a client's QR code",
        err_invalid_qr: 'Invalid QR code — no clientId found',
        err_camera_denied: 'Camera access denied',
        done: 'Done',
        reward_earned: '🎉 Free reward earned!',
        redeem_reward: 'Redeem Reward',

        // Legal
        legal: 'Legal',
        and: 'and',
        accept_privacy_prefix: 'I accept the',
        err_accept_privacy: 'You must accept the Privacy Policy and Terms of Service to create an account.',
        legal_last_updated: 'Last updated',

        // Privacy Policy
        privacy_policy_title: 'Privacy Policy',
        pp_intro_title: '1. Introduction',
        pp_intro_text: 'Welcome to Wafiyo. This Privacy Policy explains how we collect, use, store, and protect your personal information when you use our loyalty card management platform ("the Service"). By using our Service, you consent to the data practices described in this policy. This policy complies with the EU General Data Protection Regulation (GDPR) and other applicable data protection laws.',
        pp_data_collected_title: '2. Information We Collect',
        pp_data_collected_text: 'We collect the following types of personal information to provide and improve our services:',
        pp_data_item_name: 'Full name — to identify your account and personalize your experience.',
        pp_data_item_email: 'Email address — for account creation, login, and service-related communications.',
        pp_data_item_phone: 'Phone number (optional) — for additional contact if provided by the user.',
        pp_data_item_visits: 'Visit history — to track loyalty card progress and reward eligibility.',
        pp_data_item_payment: 'Payment information — processed securely by our payment provider Polar; we do not store credit card details.',
        pp_how_we_use_title: '3. How We Use Your Information',
        pp_how_we_use_text: 'We use your personal data solely for the purpose of providing and improving our loyalty card management service. This includes managing user accounts, tracking visit history, issuing rewards, processing payments, and communicating important service updates. We do not use your data for advertising or marketing purposes unrelated to our service. The legal basis for processing is the performance of a contract (Article 6(1)(b) GDPR) and your consent (Article 6(1)(a) GDPR).',
        pp_data_protection_title: '4. Data Security',
        pp_data_protection_text: 'We take the security of your data seriously. All data is transmitted via encrypted connections (HTTPS/TLS) and stored using industry-standard security measures provided by our infrastructure partners. Access to personal data is restricted to authorized personnel only, and we implement row-level security policies at the database level. In the event of a data breach, we will notify affected users and relevant authorities within 72 hours as required by the GDPR.',
        pp_third_parties_title: '5. Third-Party Services',
        pp_third_parties_text: 'We use the following third-party services to operate our platform: (1) Supabase — for authentication, database storage, and API services; (2) Polar — for payment processing and subscription management; (3) Resend — for transactional email delivery; (4) Google — for Google Wallet loyalty card integration. Each of these services processes data in compliance with their own privacy policies and applicable data protection laws. We do not sell, rent, or share your personal information with any other third parties for their commercial purposes.',
        pp_cookies_title: '6. Cookies and Local Storage',
        pp_cookies_text: 'We use essential cookies and local storage to maintain your authentication session and remember your language preferences. We do not use tracking cookies, analytics cookies, or any third-party advertising cookies. These essential cookies are strictly necessary for the operation of the Service and cannot be disabled.',
        pp_data_retention_title: '7. Data Retention',
        pp_data_retention_text: 'We retain your personal data for as long as your account is active or as needed to provide you with our services. If you delete your account, we will remove your personal data within 30 days, except where we are required to retain it for legal or regulatory purposes. Visit and loyalty card data associated with your business will also be deleted upon account removal.',
        pp_user_rights_title: '8. Your Rights (GDPR)',
        pp_user_rights_text: 'Under the GDPR, you have the following rights: (a) Right of Access — obtain a copy of your personal data; (b) Right to Rectification — correct inaccurate personal data; (c) Right to Erasure — request deletion of your personal data; (d) Right to Restriction — restrict the processing of your data; (e) Right to Data Portability — receive your data in a structured, machine-readable format; (f) Right to Object — object to processing of your personal data; (g) Right to Lodge a Complaint — file a complaint with a supervisory data protection authority. To exercise any of these rights, please contact us at support@wafiyo.com. We will respond to your request within 30 days.',
        pp_international_title: '9. International Data Transfers',
        pp_international_text: 'Your data may be processed in countries outside the European Economic Area (EEA) by our third-party service providers. In such cases, appropriate safeguards are in place, including Standard Contractual Clauses approved by the European Commission, to ensure that your data is protected in accordance with EU data protection standards.',
        pp_contact_title: '10. Contact',
        pp_contact_text: 'If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at support@wafiyo.com.',

        // Terms of Service
        tos_title: 'Terms of Service',
        tos_acceptance_title: '1. Acceptance of Terms',
        tos_acceptance_text: 'By accessing or using the Wafiyo platform ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to all of these terms, you may not access or use our Service. These terms constitute a legally binding agreement between you ("the User") and Wafiyo ("the Company").',
        tos_service_title: '2. Description of Service',
        tos_service_text: 'Wafiyo provides a digital loyalty card management platform that enables business owners to create loyalty programs, track customer visits, and manage rewards. The Service includes features such as QR code scanning, digital card generation (Google Wallet and Apple Wallet), customer relationship tools, and analytics dashboards. Features may be added, modified, or removed at our discretion.',
        tos_accounts_title: '3. User Accounts',
        tos_accounts_text: 'You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to provide accurate and complete information when creating your account and to keep this information up to date. You must notify us immediately of any unauthorized use of your account. You must be at least 18 years old or have reached the age of legal majority in your jurisdiction to use this Service.',
        tos_payment_title: '4. Payments and Subscriptions',
        tos_payment_text: 'Certain features of the Service require a paid subscription. Payments are processed by our authorized payment provider, Polar.sh. By subscribing, you authorize Polar to charge the applicable fees to your chosen payment method. Subscriptions automatically renew at the end of each billing cycle unless canceled before the renewal date. Prices may change with 30 days\' prior notice. All prices are displayed in euros (EUR) and include applicable taxes as determined by Polar based on your location.',
        tos_usage_title: '5. Acceptable Use',
        tos_usage_text: 'You agree to use the Service only for lawful purposes and in accordance with these terms. You shall not: (a) use the platform to store or transmit any content that is unlawful, harmful, threatening, abusive, or otherwise objectionable; (b) attempt to gain unauthorized access to the Service, other user accounts, or related systems; (c) use the Service to send spam or unsolicited communications; (d) reverse engineer, decompile, or disassemble any part of the Service; (e) use automated tools to scrape or extract data from the Service.',
        tos_ip_title: '6. Intellectual Property',
        tos_ip_text: 'The Service, including its design, code, features, and branding, is owned by Wafiyo and protected by intellectual property laws. You retain ownership of any content or data you upload to the Service. By using the Service, you grant us a limited license to process and display your content solely for the purpose of operating the Service.',
        tos_termination_title: '7. Termination',
        tos_termination_text: 'We reserve the right to suspend or terminate your access to the Service at any time for violation of these terms, non-payment, or any other reason we deem necessary. Upon termination, your right to use the Service will immediately cease. You may request deletion of your account at any time. We will retain your data for 30 days after termination to allow for potential reactivation, after which it will be permanently deleted.',
        tos_liability_title: '8. Limitation of Liability',
        tos_liability_text: 'The Service is provided on an "as is" and "as available" basis without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, and non-infringement. To the maximum extent permitted by law, Wafiyo shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of the Service. Our total liability for any claim related to the Service shall not exceed the total amount paid by you for the Service during the twelve (12) months preceding the claim.',
        tos_governing_title: '9. Governing Law and Jurisdiction',
        tos_governing_text: 'These Terms of Service shall be governed by and construed in accordance with the laws of Spain. Any disputes arising from the use of the Service shall be submitted to the exclusive jurisdiction of the courts of Spain. If any provision of these terms is found to be unenforceable, the remaining provisions will remain in full force and effect.',
        tos_changes_title: '10. Changes to Terms',
        tos_changes_text: 'We reserve the right to modify these terms at any time. Continued use of the Service after any changes constitutes your acceptance of the new terms. We will notify users of material changes via email or through the Service at least 30 days before such changes take effect.',
        tos_contact_title: '11. Contact',
        tos_contact_text: 'If you have any questions about these Terms of Service, please contact us at support@wafiyo.com.',

        // Refund Policy
        refund_policy_title: 'Refund Policy',
        rp_intro_title: '1. Overview',
        rp_intro_text: 'At Wafiyo, we strive to provide the best digital loyalty card experience for your business. This Refund Policy outlines the terms under which refunds may be granted for our services. All payments are processed through Polar.sh, our authorized Merchant of Record.',
        rp_trial_title: '2. Free Trial',
        rp_trial_text: 'We offer a 30-day free trial for new subscribers. During the trial period, you have full access to all features at no cost. You will not be charged until the trial period ends. If you cancel before the trial expires, you will not be charged.',
        rp_subscriptions_title: '3. Paid Subscriptions',
        rp_subscriptions_text: 'After the trial period, your subscription will automatically renew at the applicable rate. You may cancel your subscription at any time through the customer portal. Cancellations will take effect at the end of the current billing cycle, and you will retain access to the Service until that date. We do not provide prorated refunds for partial billing periods.',
        rp_refund_window_title: '4. Refund Window',
        rp_refund_window_text: 'If you are unsatisfied with the Service, you may request a full refund within 14 days of your first paid charge (not during the free trial). To request a refund, contact us at support@wafiyo.com with your account email and the reason for your request. Refunds after the 14-day window will be considered on a case-by-case basis.',
        rp_exceptions_title: '5. Service Disruptions',
        rp_exceptions_text: 'If you have experienced a significant service interruption or technical error attributable to our platform that prevented you from using the Service for more than 48 consecutive hours, you may be entitled to a prorated credit or refund for the affected period. Such requests must be submitted within 7 days of the incident.',
        rp_how_title: '6. How Refunds Are Processed',
        rp_how_text: 'Approved refunds will be processed through Polar and returned to the original payment method. Refunds typically take 5-10 business days to appear on your statement, depending on your bank or credit card provider.',
        rp_contact_title: '7. Contact Us',
        rp_contact_text: 'If you believe you are entitled to a refund or have questions about this policy, please contact us at support@wafiyo.com.',

        // About Us
        about_title: 'About Us',
        about_who_we_are_title: '1. Who We Are',
        about_who_we_are_text: 'Wafiyo is a modern digital loyalty platform designed to help local businesses connect with their customers. We believe that building customer loyalty shouldn\'t require expensive software or complex integrations. Our mission is to make premium loyalty programs accessible to businesses of all sizes.',
        about_mission_title: '2. Our Mission',
        about_mission_text: 'We aim to replace outdated paper punch cards with a seamless, eco-friendly digital experience. By providing businesses with easy-to-use tools and actionable insights, we help them increase customer retention and grow their revenue organically.',
        about_why_choose_us_title: '3. Why Choose Us?',
        about_why_choose_us_text: 'We focus on simplicity and effectiveness. With Wafiyo, there\'s no app for your customers to download—just a simple QR scan to earn points and claim rewards. For business owners, our intuitive dashboard makes tracking success effortless.',
        about_contact_title: '4. Get in Touch',
        about_contact_text: 'We are always looking to improve and love hearing from our users. If you have any feedback or want to learn more about how Wafiyo can help your business, don\'t hesitate to reach out at support@wafiyo.com.',

        // Contact
        contact_title: 'Contact Us',
        contact_message: 'For any questions or problems, you can reach us via these channels:',
        contact_via_email: 'Contact us via Email',
        email_copied: 'Email copied to clipboard!',
        copy_email: 'Copy',
        follow_us_instagram: 'Contact us on Instagram',
        // FAQ
        faq_title: 'Frequently Asked Questions',
        faq_q1: 'Is Wafiyo really free?',
        faq_a1: 'We offer a 30-day free trial with our monthly plan so you can experience all our professional features. You choose your plan before starting, and you won\'t be charged until the trial period ends. You can cancel anytime — no commitment, no questions asked.',
        faq_q2: 'Do my customers need to download an app?',
        faq_a2: 'No app download is required! Customers simply scan your QR code and the card opens in their mobile browser. They can also save it to Google Wallet or Apple Wallet for easy access.',
        faq_q3: 'Is my data secure?',
        faq_a3: 'Yes, we take security seriously. All data is encrypted and stored using industry-standard security measures. You also maintain 100% ownership of your customer data.',
        faq_q4: 'How do I reward my customers?',
        faq_a4: 'When a customer visits, you just scan the QR code on their digital card using your phone. Our system tracks their visits automatically and notifies you when they are due for a reward.',
        faq_q5: 'Can I customize my digital card?',
        faq_a5: 'Absolutely! Our professional plans allow you to customize the card colors, add your own logo, and set your own reward thresholds to match your branding.',
        // Past Due
        past_due_title: 'Payment Failed',
        past_due_desc: 'We were unable to process your most recent subscription payment. To avoid service interruption and regain access to your dashboard, please update your payment method.',
        past_due_btn: 'Update Payment Method',
        past_due_portal_err: 'Unable to open billing portal. Please contact support.',

        // Onboarding Checklist
        onboarding_title: 'Quick Setup Guide',
        onboarding_subtitle: 'Complete these 4 quick steps to launch your loyalty card and start welcoming customers.',
        onboarding_completed_title: 'Setup Complete! 🎉',
        onboarding_completed_desc: 'Your loyalty card is fully live and ready for your customers.',
        onboarding_dismiss_hint: 'You can reopen this checklist anytime from Settings.',
        onboarding_need_client_first: 'Add or scan your test card first in Step 2!',
        onboarding_all_done_headline: "You're 100% ready for business!",
        onboarding_all_done_sub: 'Your loyalty program is live. Place your stand at the register and welcome your customers.',
        onboarding_dismiss_btn: 'Hide Guide & Start Growing',
        onboarding_reopen_btn: 'Quick Setup Guide',
        onboarding_reopened: 'Quick setup guide reopened on your dashboard!',
        onboarding_step1_title: 'Design Your Loyalty Card',
        onboarding_step1_desc: 'Choose your brand colors, stamp icon, and set your reward threshold.',
        onboarding_step1_btn: 'Customize Card',
        onboarding_step2_title: 'Add Test Card to Your Phone',
        onboarding_step2_desc: 'See how your card looks in Apple or Google Wallet. Open on your phone or scan with your camera.',
        onboarding_step2_btn: 'Get Test Card',
        onboarding_step3_title: 'Test Stamping a Visit',
        onboarding_step3_desc: 'Experience the instant wallet update. Stamp your test client in 1 click or use the camera scanner.',
        onboarding_step3_stamp_btn: '⚡ +1 Test Stamp',
        onboarding_step3_scan_btn: 'Open Scanner',
        onboarding_step4_title: 'Get Your Counter QR Stand',
        onboarding_step4_desc: 'Print your ready-to-use QR stand or display it at your cash register for customers to scan.',
        onboarding_step4_btn: 'Download Counter Stand',
        onboarding_got_it: 'I got it!',
        ready_to_print: 'Print-ready',
        downloaded: 'Downloaded',
        completed: 'done',
        collapse: 'Collapse',
        expand: 'Expand',
        dismiss: 'Dismiss',
        view_qr: 'View QR',
        qr_modal_tip_title: 'How to test:',
        qr_modal_tip_desc: 'On mobile, tap "Open on this phone". On computer, scan the QR code with your smartphone camera.',
        open_on_device: 'Open on this phone',
        copy_link: 'Copy Link',
        link_copied: 'Registration link copied to clipboard!',
        download_poster: 'Download Counter Stand (Printable)',
        onboarding_cartel_what_title: 'What is this?',
        onboarding_cartel_what_desc: 'A ready-to-print QR stand for your counter or cash register.',
        onboarding_cartel_how_title: 'How to use it:',
        onboarding_cartel_how_desc: 'Print and display it where customers pay. They scan it with their phone camera to join in 3 seconds — no app download needed.',
        onboarding_client_preview_title: 'Customer Pass View',
        onboarding_client_preview_desc: 'This is the exact pass and wallet buttons your clients receive:'
    },
    es: {
        // General
        cancel: 'Cancelar',
        close: 'Cerrar',
        loading: 'Cargando…',
        search: 'Buscar por nombre o correo…',
        business_not_found: 'Negocio no encontrado.',
        client_label: 'Cliente',
        visits_label: 'Visitas',
        stamp_n: 'Sello {{count}}',
        err_biz_loading: 'Espera un momento... Todavía estamos obteniendo los detalles de tu negocio.',
        hello_prefix: 'Hola',
        now: 'Ahora',
        preview: 'Vista Previa',
        apple: 'Apple',
        google: 'Google',
        name: 'Nombre',
        until_reward: 'HASTA RECOMPENSA',
        stamps_suffix: 'sellos',
        ready: '¡Listo!',
        email: 'Correo',
        logo: 'Logo',
        design: 'Diseño',

        // Navbar
        my_qr_code: '🏪 Mi Código QR',
        scan_qr: '📷 Escanear QR',
        add_client: '➕ Añadir Cliente',
        sign_out: 'Cerrar Sesión',
        dashboard: 'Dashboard',
        back: 'Volver',
        card_design: 'Diseño de Tarjeta',

        // Landing
        landing_subtitle: 'El hermoso sistema de tarjetas de fidelidad digitales. Mantén a tus clientes regresando sin tarjetas de papel.',
        get_started_free: 'Comienza Gratis',
        see_how_it_works: 'Ver Cómo Funciona',
        how_it_works: 'Cómo funciona',
        step_1_title: '1. Registra tu negocio',
        step_1_desc: 'Crea una cuenta en segundos. Obtendrás un código QR único para el mostrador de tu tienda.',
        step_2_title: '2. Clientes escanean',
        step_2_desc: 'Sin aplicaciones. Los clientes escanean tu QR para registrar al instante su tarjeta digital.',
        step_3_title: '3. Escanea y premia',
        step_3_desc: 'Cuando te visiten, escanea su teléfono para añadir un sello. 10 sellos = ¡Recompensa gratis!',
        features_title: '¿Por qué elegir nuestra plataforma?',
        feat_1_title: 'Sin Aplicaciones',
        feat_1_desc: 'Funciona en el navegador. Los clientes solo escanean y guardan en Google o Apple Wallet.',
        feat_2_title: 'Configuración Instantánea',
        feat_2_desc: 'Empieza en minutos. Sin necesidad de hardware o integraciones de puntos de venta.',
        feat_3_title: 'Datos Analíticos',
        feat_3_desc: 'Conoce exactamente quiénes son tus mejores clientes y qué tan seguido visitan.',
        ready_to_start: '¿Listo para modernizar tu programa de lealtad?',
        create_business_account: 'Crea Cuenta de Negocio',
        go_to_dashboard: 'Ir al Panel',
        landing_badge: '✨ La tarjeta de sellos digital #1',
        hero_title_1: 'Lealtad a la antigua,',
        hero_title_2: 'reimaginada para la era digital.',
        customer_rating: 'Valoración de clientes',
        apps_to_download: 'Apps para descargar',
        data_ownership: 'Propiedad de los datos',
        how_it_works_subtitle: 'Simple de usar. Fácil de configurar. Gestionar tu programa de lealtad nunca ha sido tan sencillo.',
        footer_tagline: 'Empoderando a negocios con soluciones de lealtad digitales modernas.',
        product: 'Producto',
        features: 'Características',
        company: 'Empresa',
        about_us: 'Sobre Nosotros',
        contact: 'Contacto',
        all_rights_reserved: 'Todos los derechos reservados.',

        // Redesign Sections
        announcement_text: '🏆 La tarjeta de sellos digital #1 — Sin apps. Sin papel. Solo resultados.',
        promo_banner: '🎁 ¡Consigue 1 mes de prueba gratis! Oferta termina el 1 de diciembre',
        see_more: 'Ver más →',
        pill_badge: '✦ Tarjetas digitales para tu negocio',
        hero_h1: 'Cada cliente que no regresa es dinero que pierdes.',
        hero_sub: 'Crea tarjetas de sellos digitales en minutos. Tus clientes las guardan en Apple o Google Wallet — sin descargar ninguna app.',
        cta_free: 'Obtén un tour gratis en la app',
        cta_demo: 'Ver planes',
        demo_banner_title: 'Modo Demo: Wafiyo Cafe',
        demo_banner_text: 'Explora el dashboard con datos de prueba. Los cambios no se guardarán.',
        install_banner_title: 'Instalar Wafiyo',
        install_banner_android: 'Añade Wafiyo a tu pantalla de inicio para acceder rápido.',
        install_banner_ios: 'Toca el botón Compartir, luego "Añadir a pantalla de inicio".',
        install_banner_btn: 'Instalar',
        cta_real_signup: 'Empezar Prueba Real →',
        demo_feature_restricted: 'La integración con Wallet está desactivada en modo demo.',
        demo_sidebar_cta: '¿Listo para lanzar tu programa de fidelidad real?',
        trust_1: 'Gratis para empezar',
        trust_2: 'Prueba gratuita de 30 días',
        trust_3: 'Configuración en 5 min',
        social_proof: 'Con la confianza de negocios en todo el mundo',
        problem_h2: 'Las tarjetas de papel están rotas',
        pain_1_title: 'Se pierden o se olvidan en casa',
        pain_1_desc: 'Los clientes las pierden u olvidan, rompiendo el ciclo de fidelidad.',
        pain_2_title: 'No sabes quiénes son tus mejores clientes',
        pain_2_desc: 'Es imposible rastrear quién visita más sin herramientas digitales.',
        pain_3_title: 'Los clientes no vuelven después de la primera visita',
        pain_3_desc: 'Sin una razón para volver, los clientes ocasionales desaparecen.',
        transition_text: 'Wafiyo resuelve todo esto — en menos de 5 minutos.',
        how_it_works_h2: 'Cómo funciona',
        step_1_title: '1. Registra tu negocio',
        step_1_desc: 'Crea tu cuenta y obtén tu QR único.',
        step_2_title: '2. Tus clientes escanean',
        step_2_desc: 'Se unen al instante desde el navegador.',
        step_3_title: '3. Escanea y recompensa',
        step_3_desc: 'Tú escaneas su tarjeta en cada visita.',
        feat_1_title_new: 'Sin Aplicaciones',
        feat_1_desc_new: 'Tus clientes solo necesitan su teléfono. Escanean, se registran, y guardan su tarjeta en Wallet en segundos.',
        feat_2_title_new: 'Diseña tu tarjeta',
        feat_2_desc_new: 'Personaliza colores, logo e iconos de sellos para que refleje tu marca.',
        feat_3_title_new: 'Panel de Analytics',
        feat_3_desc_new: 'Un panel completo de analíticas con gráficos de barras, áreas y donut. Controla visitas por día, hora y mes. Mide tu retención y conversión de un vistazo.',
        feat_4_title_new: 'Insights Inteligentes',
        feat_4_desc_new: 'Descubre tu mejor día de la semana, tus horas pico y qué clientes están a un sello de su recompensa. Decisiones basadas en datos, simplificadas.',
        metric_1_val: '40%',
        metric_1_label: 'Tasa de conversión media',
        metric_2_val: '0 apps',
        metric_2_label: 'Descargadas por tus clientes',
        metric_3_val: '5 min',
        metric_3_label: 'Para configurar tu programa',
        testi_h2: 'Lo que dicen nuestros negocios',
        pricing_toggle_mo: 'Mensual',
        pricing_toggle_yr: 'Anual',
        popular_badge: 'Más popular',
        cancel_anytime: '✓ Prueba gratuita incluida. Sin permanencia. Cancela cuando quieras.',
        cta_monthly: 'Empieza tu prueba gratis',
        cta_yearly: 'Ahorra con el plan anual',
        // Analytics page
        analytics_visits_this_week: 'Visitas Esta Semana',
        analytics_retention_rate: 'Tasa de Retención',
        analytics_stamps_given: 'Sellos Entregados',
        analytics_conversion_rate: 'Tasa de Conversión',
        analytics_visits_per_day: 'Visitas por día',
        analytics_tab_week: 'Semana',
        analytics_tab_month: 'Mes',
        analytics_tab_year: 'Año',
        analytics_visits_per_hour: 'Visitas por hora',
        analytics_client_retention: 'Retención de Clientes',
        analytics_returning: 'Recurrentes',
        analytics_new: 'Nuevos',
        analytics_top_clients: 'Clientes más activos',
        analytics_visits: 'visitas',
        analytics_stamps: 'sellos',
        analytics_no_clients: 'Sin clientes aún',
        analytics_best_day: 'Mejor Día',
        analytics_peak_hour: 'Hora Pico',
        analytics_upcoming_rewards: 'Próximas Recompensas',
        analytics_total_visits: 'visitas en total',
        analytics_clients: 'clientes',
        analytics_one_stamp_away: 'A 1 sello de su recompensa',
        last_visit: 'Última visita',
        no_visits_yet: 'Sin visitas aún',
        time_ago: 'atrás',
        time_just_now: 'Ahora mismo',
        notif_title: 'Enviar Notificación',
        notif_placeholder: 'Escribe tu mensaje...',
        notif_send: 'Enviar a',
        notif_clients_suffix: 'clientes',
        notif_sending: 'Enviando...',
        notif_cooldown: 'Próxima notificación disponible en',
        notif_char_limit: 'caracteres',
        notif_last_sent: 'Último envío',
        notif_success: '¡Notificación enviada con éxito!',
        notif_empty: 'Escribe un mensaje',
        notif_no_clients: 'No hay clientes para notificar',
        notif_remaining: 'restantes hoy',
        cookie_text: 'Usamos cookies para mejorar tu experiencia. Puedes aceptar o rechazar las cookies no esenciales.',
        cookie_policy_link: 'Política de Privacidad',
        cookie_accept: 'Aceptar',
        cookie_decline: 'Rechazar',
        analytics_title: 'Estadísticas',
        view_all: 'Ver Detalles',
        sp_cafes: 'Cafeterías',
        sp_barbers: 'Barberías',
        sp_restaurants: 'Restaurantes',
        sp_shops: 'Tiendas',
        salon_social_proof: 'Con la confianza de los mejores profesionales de la belleza',
        salon_sp_1: 'Peluquerías',
        salon_sp_2: 'Centros de Uñas',
        salon_sp_3: 'Clínicas Estéticas',
        salon_sp_4: 'Spas y Bienestar',
        mock_best_day: 'Mejor Día',
        mock_saturday: 'Sábado',
        mock_peak_hour: 'Hora Pico',
        mock_upcoming_rewards: 'Próximas Recompensas',
        mock_clients_count: '3 clientes',
        feat_5_title_new: 'Notificaciones Push por Wallet',
        feat_5_desc_new: 'Envía mensajes personalizados directamente al teléfono de tus clientes. Tu mensaje aparece en su pantalla de bloqueo a través de Apple Wallet y Google Wallet — sin necesidad de app.',
        feat_5_mock_msg: '¡Happy hour hoy! 🔥',
        feat_5_mock_sent: 'Enviado a 47 clientes ✓',
        feat_5_mock_lock: 'Aparece en pantalla de bloqueo',
        push_notifications_feature: 'Notificaciones Push',
        hero_push_bold: '¡Te falta 1 visita!',
        hero_push_body: 'Ven esta semana y consigue tu próximo café gratis en Tormen Cafetería.',
        hero_push_meta: 'Notificación push · hace 2 horas',
        hero_social_proof: '+200 negocios',
        hero_social_proof_suffix: 'ya usan Wafiyo en todo el mundo',
        final_cta_h2: '¿Listo para modernizar tu programa de fidelidad?',
        final_cta_sub: 'Únete a los negocios que ya fidelizan sin papel.',
        final_cta_btn: 'Empieza Gratis Ahora',
        final_cta_micro: 'Prueba gratuita de 30 días · Configuración en 5 minutos',
        salon_hero_h1: 'Cada cita vacía es dinero que pierdes.',
        salon_hero_sub: 'Llena tu agenda enviando notificaciones push directo al móvil de tus clientas. Tarjetas en Google y Apple Wallet. Sin apps.',
        salon_hero_push_bold: '¡Tenemos un hueco a las 17:00!',
        salon_hero_push_body: 'Reserva ahora y consigue el doble de sellos en Wafiyo Salón.',
        salon_pain_1_title: 'Las tarjetas de papel se olvidan',
        salon_pain_1_desc: 'Tus clientas olvidan sus tarjetas, rompiendo el hábito de volver a tu salón.',
        salon_pain_2_title: 'Cancelaciones sin cubrir',
        salon_pain_2_desc: 'Cuando una clienta cancela, no tienes forma directa de avisar a otras para llenar el hueco.',
        salon_pain_3_title: 'Sin contacto entre citas',
        salon_pain_3_desc: 'Sin un recordatorio o razón para volver, las clientas casuales nunca se hacen habituales.',
        salon_feat_5_mock_msg: '¡Promoción flash! 20% en manicura hoy.',
        salon_hero_social_proof: '+150 salones de belleza',
        salon_testi_1_quote: 'Wafiyo me ayuda a llenar cancelaciones de última hora con una sola notificación push a mis clientas. Es increíble.',
        salon_testi_1_type: 'Salón de Belleza y Peluquería',
        salon_testi_2_quote: 'Desde que pasamos a las tarjetas digitales, mis clientas nunca las olvidan. Hemos visto un aumento del 30% en las visitas de manicura.',
        salon_testi_2_type: 'Estudio de Uñas y Spa',
        salon_testi_3_quote: 'El retorno de inversión es asombroso. Una notificación push rápida sobre nuestra promo del jueves llena nuestra agenda cada semana.',
        salon_testi_3_type: 'Clínica Estética',
        salon_urgency_banner: 'Oferta Especial: 30 Días Gratis (Quedan 12 plazas)',
        salon_feat_1_title: 'Llena tus horas muertas al instante',
        salon_feat_1_desc: 'Envía una notificación push al móvil de tus clientas cuando tengas una cancelación de última hora. Convierte las horas vacías en citas.',
        salon_feat_2_title: 'Tarjetas que nunca se pierden',
        salon_feat_2_desc: 'Tus clientas siempre llevarán tu tarjeta en su Apple o Google Wallet. Se acabaron las tarjetas de cartón olvidadas en casa.',
        salon_feat_3_title: 'Conoce a tus mejores clientas',
        salon_feat_3_desc: 'Descubre quiénes vienen más y qué días son los más flojos. Premia su fidelidad y asegúrate de que siempre vuelvan a tu salón.',
        salon_feat_4_title: 'Tu salón, tu marca',
        salon_feat_4_desc: 'Diseña la tarjeta con el logo y los colores de tu salón. Crea una experiencia profesional y elegante que destaque.',

        // Login
        back_to_home: 'Volver al Inicio',
        loyalty_card_manager: 'Wafiyo',
        sign_in_business: 'Inicia sesión en tu cuenta de negocio',
        create_biz_account: 'Crea tu cuenta de negocio',
        business_name: 'Nombre del Negocio',
        email: 'Correo Electrónico',
        password: 'Contraseña',
        please_wait: 'Por favor, espera…',
        sign_in: 'Ingresar',
        create_account: 'Crear Cuenta',
        dont_have_account: '¿No tienes una cuenta? ',
        already_have_account: '¿Ya tienes una cuenta? ',
        sign_up_link: 'Registrarse',
        forgot_password_link: '¿Olvidaste tu contraseña?',
        forgot_pw_title: 'Restablecer contraseña',
        forgot_pw_subtitle: 'Introduce tu email y te enviaremos un enlace',
        forgot_pw_success: 'Revisa tu correo, te hemos enviado un enlace para restablecer tu contraseña',
        send_link: 'Enviar enlace',
        err_email_not_found: 'No encontramos ninguna cuenta con este email',
        reset_pw_title: 'Nueva contraseña',
        reset_pw_subtitle: 'Introduce tu nueva contraseña',
        reset_pw_label: 'Nueva contraseña',
        reset_pw_confirm: 'Confirmar contraseña',
        reset_pw_update_btn: 'Actualizar contraseña',
        err_pw_match: 'Las contraseñas no coinciden',
        err_pw_length: 'La contraseña debe tener al menos 8 caracteres',
        reset_pw_success: 'Contraseña actualizada correctamente',
        redirecting_login: 'Redirigiendo al inicio de sesión...',
        verifying: 'Verificando...',
        account_confirmed: '¡Cuenta confirmada!',
        account_confirmed_msg: 'Tu cuenta ha sido verificada correctamente',
        invalid_link: 'Enlace inválido',
        invalid_link_msg: 'Este enlace ha expirado o ya fue usado. Vuelve a registrarte o contacta soporte.',
        go_to_dashboard: 'Ir al dashboard',
        back_to_login: '← Volver al inicio de sesión',
        check_email_in: 'Revisa tu correo en',
        check_email_msg: 'Te hemos enviado un enlace de confirmación para activar tu cuenta.',
        check_spam: '¿No lo ves? Revisa la carpeta de spam',
        i_confirmed_already: 'Ya he confirmado mi correo',
        err_confirm_email_first: 'Confirma tu email antes de continuar',
        back_to_register: 'Volver al registro',

        // Pricing
        choose_your_plan: 'Elige tu plan',
        pricing_subtitle: 'Libera todo el potencial de tu negocio con nuestro sistema de fidelización profesional.',
        monthly_plan: 'Plan Mensual',
        yearly_plan: 'Plan Anual',
        month: 'mes',
        first_month: 'primer mes',
        then_price: 'Luego {{symbol}}{{amount}}/mes',
        year: 'año',
        days_free_trial: 'días de prueba gratis',
        migration_pricing_title: 'Actualización del sistema de pago',
        migration_pricing_desc: 'Hemos cambiado de proveedor de pagos. Por favor, selecciona un plan para continuar usando Wafiyo. Como ya has usado tu prueba gratuita, el cobro se realizará de forma inmediata.',
        subscribe_now: 'Suscribirse ahora',
        choose_monthly: 'Elegir plan mensual',
        choose_yearly: 'Elegir plan anual',
        migration_alert_expired_title: '⚠️ Acción requerida',
        migration_alert_update_title: '💳 Actualiza tu método de pago',
        migration_alert_expired_desc: 'Tu suscripción ha finalizado. Reactiva tu plan {{plan}} para seguir gestionando tus tarjetas de fidelidad y clientes.',
        migration_alert_update_desc_date: 'Hemos cambiado de proveedor de pagos. Tu plan {{plan}} está activo hasta el {{date}} — solo añade tu tarjeta ahora y no se te cobrará hasta entonces. ¡Solo toma un minuto!',
        migration_alert_update_desc_nodate: 'Hemos cambiado de proveedor de pagos. Por favor, añade tus datos de pago para mantener tu plan {{plan}} activo.',
        migration_btn_reactivate: 'Reactivar plan {{plan}}',
        migration_btn_secure: 'Asegurar mi plan {{plan}}',
        activating: 'Activando...',

        // ROI Calculator
        roi_section_title: 'Descubre el impacto en tu negocio',
        roi_title: 'Calcula tu ROI',
        roi_desc: 'Mira cuántos ingresos estás perdiendo por no fidelizar a tus clientes.',
        roi_daily_customers: 'Clientes Diarios',
        roi_avg_spend: 'Ticket Promedio',
        roi_extra_revenue: 'Ingresos Extra Potenciales',
        roi_per_month: 'por mes',
        roi_disclaimer: 'Basado en un aumento conservador del 12% en ingresos por retención mejorada.',
        roi_cta: 'Empieza a Recuperar Ingresos',

        unlimited_clients: 'Clientes ilimitados',
        custom_card_designs: 'Diseños de tarjeta personalizados',
        qr_code_scanning: 'Escaneo de códigos QR',
        testi_1_quote: "Wafiyo ha simplificado mi programa de fidelidad. Mis clientes aman no tener que guardar otro papel en su billetera.",
        testi_1_type: "Salón de belleza",
        testi_2_quote: "La integración con Apple y Google Wallet es lo que nos diferencia. Es moderno, rápido y profesional.",
        testi_2_type: "Cafetería",
        testi_3_quote: "Desde que eliminamos las tarjetas físicas, la tasa de retorno de mis clientes ha subido un 20%. Increíble.",
        testi_3_type: "Restaurante",
        google_wallet_integration: 'Integración con Google y Apple Wallet',
        all_monthly_features: 'Todas las funciones mensuales',
        three_months_free: 'Incluye 3 meses gratis',
        priority_support: 'Soporte prioritario',
        best_value: 'Mejor valor',
        save_amount: '¡Ahorra {{symbol}}{{amount}} al año!',
        start_free_trial: 'Iniciar prueba gratuita de 30 días',
        free_trial_badge: '1 Mes de Prueba Gratis',
        choose_yearly: 'Elegir plan anual',
        pricing_footer_text: 'Todos los planes incluyen sellos ilimitados, historial de escaneo y analíticas básicas.',

        // Dashboard
        total_clients: 'Total de Clientes',
        total_visits: 'Total de Visitas',
        rewards_due: 'Recompensas',
        clients: 'Clientes',
        no_clients_yet: 'Sin clientes aún — haz clic en "Añadir Cliente" para empezar.',
        no_clients_match: 'Ningún cliente coincide.',
        loading_clients: 'Cargando clientes…',
        visits_today: 'Visitas Totales',
        rewards: 'Recompensas',
        conversion: 'Conversión',
        conversion_tooltip: 'Cálculo: (Sellos Totales / Visitas Totales) * 100. Muestra cuántos visitantes se convierten en sellos activos.',
        clients_tooltip: 'Número total de tarjetas de fidelidad distribuidas a tus clientes.',
        visits_tooltip: 'Número total de sellos o visitas registradas en todos los clientes.',
        rewards_tooltip: 'Número de clientes que han ganado suficientes sellos y están listos para reclamar su recompensa gratuita.',
        recent_activity: 'Actividad Reciente',
        no_recent_activity: 'No hay actividad reciente',
        no_clients_found: 'No se encontraron clientes',
        settings: 'Ajustes',
        business_account: 'Cuenta del Negocio',
        user_email: 'Email de Usuario',
        subscription: 'Suscripción',
        plan_status: 'Estado del Plan',
        active: 'Activo',
        inactive: 'Inactivo',
        manage_subscription: 'Gestionar Suscripción',
        activity_visit: 'Visita registrada',
        activity_reg: 'Nuevo cliente',
        no_clients_to_export: 'No hay clientes para exportar',
        csv_export_success: 'CSV exportado correctamente',
        download_csv: 'Descargar CSV',

        // Client Card
        progress_toward_reward: 'Progreso hacia recompensa',
        visits: 'visitas',
        reward_due: '🎉 Premio Listo',
        record_visit: 'Añadir Visita',
        gift: '🎁 Regalo',
        remove_visit: 'Quitar',
        remove_visit_confirm: '¿Estás seguro de que quieres quitar 1 visita a',
        hide_qr: 'Ocultar QR',
        show_qr: '🔲 Ver QR',
        wallet_link: '📱 Enlace Wallet',
        client_scans_this: 'El cliente escanea esto para ver su tarjeta',
        no_contact_info: 'Sin contacto',

        // Add Client Modal
        add_new_client: '➕ Nuevo Cliente',
        card_created: '✅ Tarjeta Creada',
        client_name: 'Nombre del Cliente *',
        optional: '(Opcional)',
        phone: 'Teléfono',
        visits_for_free_reward: 'Visitas para premio',
        create_card: 'Crear Tarjeta',
        creating: 'Creando…',
        share_with: 'Comparte este QR o enlace con',
        client_registration_qr: 'QR de Registro',
        add_to_wallet: 'Agregar a Google Wallet',
        add_to_apple_wallet: 'Agregar a Apple Wallet',
        wallet_not_configured: 'Wallet no configurado en este negocio.',

        // Card Customizer
        customize_card: 'Personaliza tu Tarjeta',
        card_title: 'Título de Tarjeta / Negocio',
        logo_upload: 'Logo del Negocio',
        logo_change: 'Cambiar logo',
        logo_remove: 'Eliminar',
        logo_upload_hint: 'PNG, JPG o WEBP — máx 2MB',
        err_logo_too_large: 'El archivo no puede superar 2MB',
        err_logo_wrong_type: 'Solo se permiten imágenes PNG, JPG o WEBP',
        err_logo_upload_failed: 'Error al subir el logo, inténtalo de nuevo',
        color_preset: 'Ajustes de Marca',
        background_color: 'Color de Tarjeta',
        text_color: 'Color de Texto',
        number_color: 'Color de Números',
        stamp_circle_color: 'Color de Sellos',
        stamp_icon: 'Icono de Sello',
        stamp_goal: 'Sellos para Premio',
        preview_stamps: 'Previsualizar Visitas',
        reward_text: 'Premio (ej. Café Gratis)',
        save_design: '💾 Publicar Diseño',
        saving: 'Publicando...',
        live_preview: 'Vista Previa',
        view_png: 'Ver como PNG →',
        wallet_preview: 'Vista Previa en Wallet',
        wallet_header: 'Elige la cabecera de tu wallet',
        design_saved_notify: '¡Éxito! El nuevo diseño de tu tarjeta ya está publicado.',

        // Business QR Modal
        business_signup_qr: '🏪 QR de Registro Local',
        print_or_display: 'Imprime o muestra este QR. Los clientes lo escanean para su tarjeta digital al instante.',
        generating_qr: 'Generando QR…',
        download: '⬇️ Descargar',
        print: '🖨️ Imprimir',
        print_poster_badge: 'Terminal de Lealtad',
        print_poster_subtitle: 'Escanea para unirte a nuestro programa de lealtad digital y comienza a ganar premios hoy.',
        print_poster_step_1: 'Escanea QR',
        print_poster_step_2: 'Regístrate',
        print_poster_step_3: 'Añadir a Wallet',
        print_poster_no_app: 'Sin aplicaciones. Solo escaneo.',
        print_poster_powered_by: 'Potenciado por Wafiyo',

        // Client Register
        join_loyalty: '¡Únete a Wafiyo — es gratis!',
        full_name: 'Nombre Completo',
        youre_in: '¡Ya estás dentro!',
        loyalty_card_ready: 'Tu tarjeta de lealtad para {{businessName}} está lista.',
        show_qr_every_visit: 'Muestra este código QR en el local cada visita',
        your_goal: 'Tu meta',
        collect_n_visits: 'Acumula {{threshold}} visitas para ganar una recompensa 🏆',
        welcome_aboard: '¡Bienvenido(a), {{clientName}}!',
        add_to_wallet_desc: 'Guarda tu tarjeta de membresía digital en tu teléfono para un acceso rápido.',

        // Notifications & Validation
        err_valid_name: 'Por favor, ingresa un nombre válido (2-50 caracteres).',
        err_valid_email: '¡Ups! Ese correo no parece estar bien. Por favor, revísalo.',
        err_password_min: 'La contraseña debe tener al menos 6 caracteres.',
        err_password_weak: '¡Casi listo! Para que tu contraseña sea más segura, por favor incluye al menos un número.',
        err_valid_phone: 'Por favor, ingresa un número de teléfono válido.',
        err_threshold_range: 'La meta de visitas debe estar entre 1 y 100.',
        notify_reg_success: '¡Registro exitoso! Por favor, revisa tu correo.',
        notify_welcome_back: '¡Bienvenido de nuevo!',
        notify_welcome_ready: '¡Bienvenido! Tu tarjeta está lista.',
        notify_visit_recorded: 'Visita registrada',
        notify_visit_removed: 'Visita eliminada',
        notify_reward_redeemed: '🎁 ¡Premio canjeado!',
        err_not_confirmed: 'El correo aún no ha sido confirmado. Haz clic en el enlace de tu bandeja de entrada.',
        confirm_remove_title: 'Eliminar Visita',
        confirm_action: 'Confirmar',

        // QR Scanner
        scan_title: '📷 Escanear QR de Cliente',
        point_camera: 'Apunta la cámara al código QR del cliente',
        err_invalid_qr: 'Código QR inválido — no se encontró el ID',
        err_camera_denied: 'Acceso a la cámara denegado',
        done: 'Listo',
        reward_earned: '🎉 ¡Premio gratis ganado!',
        redeem_reward: 'Canjear Premio',

        // Legal
        legal: 'Legal',
        and: 'y',
        accept_privacy_prefix: 'Acepto la',
        err_accept_privacy: 'Debes aceptar la Política de Privacidad y los Términos de Servicio para crear una cuenta.',
        legal_last_updated: 'Última actualización',

        // Privacy Policy
        privacy_policy_title: 'Política de Privacidad',
        pp_intro_title: '1. Introducción',
        pp_intro_text: 'Bienvenido a Wafiyo. Esta Política de Privacidad explica cómo recopilamos, usamos, almacenamos y protegemos tu información personal cuando utilizas nuestra plataforma de gestión de tarjetas de fidelización ("el Servicio"). Al usar nuestro Servicio, aceptas las prácticas de datos descritas en esta política. Esta política cumple con el Reglamento General de Protección de Datos (RGPD) de la UE y otras leyes de protección de datos aplicables.',
        pp_data_collected_title: '2. Información que Recopilamos',
        pp_data_collected_text: 'Recopilamos los siguientes tipos de información personal para proporcionar y mejorar nuestros servicios:',
        pp_data_item_name: 'Nombre completo — para identificar tu cuenta y personalizar tu experiencia.',
        pp_data_item_email: 'Correo electrónico — para la creación de cuenta, inicio de sesión y comunicaciones relacionadas con el servicio.',
        pp_data_item_phone: 'Número de teléfono (opcional) — para contacto adicional si el usuario lo proporciona.',
        pp_data_item_visits: 'Historial de visitas — para rastrear el progreso de la tarjeta de fidelización y la elegibilidad para recompensas.',
        pp_data_item_payment: 'Información de pago — procesada de forma segura por nuestro proveedor de pagos Polar; no almacenamos datos de tarjetas de crédito.',
        pp_how_we_use_title: '3. Cómo Usamos Tu Información',
        pp_how_we_use_text: 'Usamos tus datos personales únicamente con el propósito de proporcionar y mejorar nuestro servicio de gestión de tarjetas de fidelización. Esto incluye la gestión de cuentas, el seguimiento del historial de visitas, la emisión de recompensas, el procesamiento de pagos y la comunicación de actualizaciones importantes. No utilizamos tus datos con fines publicitarios o de marketing no relacionados con nuestro servicio. La base legal para el tratamiento es la ejecución de un contrato (Artículo 6(1)(b) RGPD) y tu consentimiento (Artículo 6(1)(a) RGPD).',
        pp_data_protection_title: '4. Seguridad de los Datos',
        pp_data_protection_text: 'Tomamos la seguridad de tus datos con seriedad. Todos los datos se transmiten a través de conexiones cifradas (HTTPS/TLS) y se almacenan utilizando medidas de seguridad estándar de la industria. El acceso a los datos personales está restringido únicamente al personal autorizado, e implementamos políticas de seguridad a nivel de fila en la base de datos. En caso de violación de datos, notificaremos a los usuarios afectados y a las autoridades pertinentes dentro de las 72 horas según lo exige el RGPD.',
        pp_third_parties_title: '5. Servicios de Terceros',
        pp_third_parties_text: 'Utilizamos los siguientes servicios de terceros para operar nuestra plataforma: (1) Supabase — para autenticación, almacenamiento de base de datos y servicios de API; (2) Polar — para procesamiento de pagos y gestión de suscripciones; (3) Resend — para envío de correos transaccionales; (4) Google — para la integración de tarjetas de fidelización con Google Wallet. Cada uno de estos servicios procesa datos de acuerdo con sus propias políticas de privacidad y leyes de protección de datos aplicables. No vendemos, alquilamos ni compartimos tu información personal con otros terceros para sus fines comerciales.',
        pp_cookies_title: '6. Cookies y Almacenamiento Local',
        pp_cookies_text: 'Utilizamos cookies esenciales y almacenamiento local para mantener tu sesión de autenticación y recordar tus preferencias de idioma. No utilizamos cookies de seguimiento, cookies de análisis ni cookies publicitarias de terceros. Estas cookies esenciales son estrictamente necesarias para el funcionamiento del Servicio y no pueden desactivarse.',
        pp_data_retention_title: '7. Retención de Datos',
        pp_data_retention_text: 'Conservamos tus datos personales mientras tu cuenta esté activa o según sea necesario para proporcionarte nuestros servicios. Si eliminas tu cuenta, borraremos tus datos personales en un plazo de 30 días, excepto cuando estemos obligados a conservarlos por motivos legales o regulatorios. Los datos de visitas y tarjetas de fidelización asociados a tu negocio también se eliminarán al eliminar la cuenta.',
        pp_user_rights_title: '8. Tus Derechos (RGPD)',
        pp_user_rights_text: 'Bajo el RGPD, tienes los siguientes derechos: (a) Derecho de Acceso — obtener una copia de tus datos personales; (b) Derecho de Rectificación — corregir datos personales inexactos; (c) Derecho de Supresión — solicitar la eliminación de tus datos personales; (d) Derecho de Limitación — restringir el tratamiento de tus datos; (e) Derecho a la Portabilidad — recibir tus datos en un formato estructurado y legible por máquina; (f) Derecho de Oposición — oponerte al tratamiento de tus datos personales; (g) Derecho a Presentar una Reclamación — presentar una queja ante una autoridad supervisora de protección de datos. Para ejercer cualquiera de estos derechos, contáctanos en support@wafiyo.com. Responderemos a tu solicitud en un plazo de 30 días.',
        pp_international_title: '9. Transferencias Internacionales de Datos',
        pp_international_text: 'Tus datos pueden ser procesados en países fuera del Espacio Económico Europeo (EEE) por nuestros proveedores de servicios terceros. En tales casos, se aplican las salvaguardas apropiadas, incluidas las Cláusulas Contractuales Tipo aprobadas por la Comisión Europea, para garantizar que tus datos estén protegidos de acuerdo con los estándares de protección de datos de la UE.',
        pp_contact_title: '10. Contacto',
        pp_contact_text: 'Si tienes alguna pregunta, inquietud o solicitud con respecto a esta Política de Privacidad o nuestras prácticas de datos, por favor contáctanos en support@wafiyo.com.',

        // Terms of Service
        tos_title: 'Términos de Servicio',
        tos_acceptance_title: '1. Aceptación de los Términos',
        tos_acceptance_text: 'Al acceder o utilizar la plataforma Wafiyo ("el Servicio"), aceptas estar sujeto a estos Términos de Servicio. Si no estás de acuerdo con todos estos términos, no puedes acceder ni utilizar nuestro Servicio. Estos términos constituyen un acuerdo legalmente vinculante entre tú ("el Usuario") y Wafiyo ("la Empresa").',
        tos_service_title: '2. Descripción del Servicio',
        tos_service_text: 'Wafiyo proporciona una plataforma digital de gestión de tarjetas de fidelización que permite a los propietarios de negocios crear programas de lealtad, rastrear las visitas de los clientes y gestionar recompensas. El Servicio incluye funcionalidades como escaneo de códigos QR, generación de tarjetas digitales (Google Wallet y Apple Wallet), herramientas de relación con el cliente y paneles de análisis. Las funcionalidades pueden añadirse, modificarse o eliminarse a nuestra discreción.',
        tos_accounts_title: '3. Cuentas de Usuario',
        tos_accounts_text: 'Eres responsable de mantener la confidencialidad de las credenciales de tu cuenta y de todas las actividades que ocurran bajo tu cuenta. Aceptas proporcionar información precisa y completa al crear tu cuenta y mantener esta información actualizada. Debes notificarnos inmediatamente cualquier uso no autorizado de tu cuenta. Debes tener al menos 18 años o haber alcanzado la mayoría de edad legal en tu jurisdicción para utilizar este Servicio.',
        tos_payment_title: '4. Pagos y Suscripciones',
        tos_payment_text: 'Ciertas funciones del Servicio requieren una suscripción de pago. Los pagos son procesados por nuestro proveedor autorizado, Polar.sh. Al suscribirte, autorizas a Polar a cobrar las tarifas aplicables a tu método de pago elegido. Las suscripciones se renuevan automáticamente al final de cada ciclo de facturación a menos que se cancelen antes de la fecha de renovación. Los precios pueden cambiar con 30 días de aviso previo. Todos los precios se muestran en euros (EUR) e incluyen los impuestos aplicables según determina Polar en función de tu ubicación.',
        tos_usage_title: '5. Uso Aceptable',
        tos_usage_text: 'Aceptas utilizar el Servicio solo para fines lícitos y de acuerdo con estos términos. No debes: (a) utilizar la plataforma para almacenar o transmitir contenido ilegal, dañino, amenazante, abusivo o de otra manera objetable; (b) intentar obtener acceso no autorizado al Servicio, a otras cuentas de usuario o a sistemas relacionados; (c) utilizar el Servicio para enviar spam o comunicaciones no solicitadas; (d) realizar ingeniería inversa, descompilar o desensamblar cualquier parte del Servicio; (e) utilizar herramientas automatizadas para extraer datos del Servicio.',
        tos_ip_title: '6. Propiedad Intelectual',
        tos_ip_text: 'El Servicio, incluyendo su diseño, código, funcionalidades y marca, es propiedad de Wafiyo y está protegido por las leyes de propiedad intelectual. Conservas la propiedad de cualquier contenido o datos que subas al Servicio. Al usar el Servicio, nos otorgas una licencia limitada para procesar y mostrar tu contenido únicamente con el fin de operar el Servicio.',
        tos_termination_title: '7. Terminación',
        tos_termination_text: 'Nos reservamos el derecho de suspender o terminar tu acceso al Servicio en cualquier momento por violación de estos términos, falta de pago o cualquier otra razón que consideremos necesaria. Al terminar, tu derecho a usar el Servicio cesará inmediatamente. Puedes solicitar la eliminación de tu cuenta en cualquier momento. Conservaremos tus datos durante 30 días después de la terminación para permitir una posible reactivación, después de lo cual se eliminarán permanentemente.',
        tos_liability_title: '8. Limitación de Responsabilidad',
        tos_liability_text: 'El Servicio se proporciona "tal cual" y "según disponibilidad" sin garantías de ningún tipo, ya sean expresas o implícitas, incluidas, entre otras, las garantías de comerciabilidad, idoneidad para un fin particular y no infracción. En la máxima medida permitida por la ley, Wafiyo no será responsable de ningún daño indirecto, incidental, especial, consecuente o punitivo que surja de o se relacione con tu uso del Servicio. Nuestra responsabilidad total por cualquier reclamación relacionada con el Servicio no excederá el monto total pagado por ti por el Servicio durante los doce (12) meses anteriores a la reclamación.',
        tos_governing_title: '9. Ley Aplicable y Jurisdicción',
        tos_governing_text: 'Estos Términos de Servicio se regirán e interpretarán de acuerdo con las leyes de España. Cualquier disputa que surja del uso del Servicio se someterá a la jurisdicción exclusiva de los tribunales de España. Si alguna disposición de estos términos se considera inaplicable, las disposiciones restantes permanecerán en pleno vigor y efecto.',
        tos_changes_title: '10. Cambios en los Términos',
        tos_changes_text: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. El uso continuado del Servicio después de cualquier cambio constituye tu aceptación de los nuevos términos. Notificaremos a los usuarios de cambios materiales por correo electrónico o a través del Servicio con al menos 30 días de antelación.',
        tos_contact_title: '11. Contacto',
        tos_contact_text: 'Si tienes alguna pregunta sobre estos Términos de Servicio, por favor contáctanos en support@wafiyo.com.',

        // Refund Policy
        refund_policy_title: 'Política de Reembolso',
        rp_intro_title: '1. Descripción General',
        rp_intro_text: 'En Wafiyo, nos esforzamos por ofrecer la mejor experiencia de tarjeta de fidelización digital para tu negocio. Esta Política de Reembolso describe los términos bajo los cuales se pueden otorgar reembolsos por nuestros servicios. Todos los pagos son procesados a través de Polar.sh, nuestro Merchant of Record autorizado.',
        rp_trial_title: '2. Prueba Gratuita',
        rp_trial_text: 'Ofrecemos una prueba gratuita de 30 días para nuevos suscriptores. Durante el período de prueba, tienes acceso completo a todas las funcionalidades sin coste. No se te cobrará hasta que finalice el período de prueba. Si cancelas antes de que expire la prueba, no se te cobrará.',
        rp_subscriptions_title: '3. Suscripciones Pagas',
        rp_subscriptions_text: 'Después del período de prueba, tu suscripción se renovará automáticamente a la tarifa aplicable. Puedes cancelar tu suscripción en cualquier momento a través del portal de cliente. Las cancelaciones entrarán en vigor al final del ciclo de facturación actual, y conservarás acceso al Servicio hasta esa fecha. No ofrecemos reembolsos prorrateados por períodos de facturación parciales.',
        rp_refund_window_title: '4. Ventana de Reembolso',
        rp_refund_window_text: 'Si no estás satisfecho con el Servicio, puedes solicitar un reembolso completo dentro de los 14 días posteriores a tu primer cargo pagado (no durante la prueba gratuita). Para solicitar un reembolso, contáctanos en support@wafiyo.com con el correo de tu cuenta y el motivo de tu solicitud. Los reembolsos después de la ventana de 14 días se considerarán caso por caso.',
        rp_exceptions_title: '5. Interrupciones del Servicio',
        rp_exceptions_text: 'Si has experimentado una interrupción significativa del servicio o un error técnico atribuible a nuestra plataforma que te impidió utilizar el Servicio durante más de 48 horas consecutivas, puedes tener derecho a un crédito prorrateado o reembolso por el período afectado. Dichas solicitudes deben presentarse dentro de los 7 días posteriores al incidente.',
        rp_how_title: '6. Cómo se Procesan los Reembolsos',
        rp_how_text: 'Los reembolsos aprobados se procesarán a través de Polar y se devolverán al método de pago original. Los reembolsos generalmente tardan de 5 a 10 días hábiles en aparecer en tu extracto, dependiendo de tu banco o proveedor de tarjeta de crédito.',
        rp_contact_title: '7. Contáctanos',
        rp_contact_text: 'Si crees que tienes derecho a un reembolso o tienes preguntas sobre esta política, por favor contáctanos en support@wafiyo.com.',

        // Sobre Nosotros
        about_title: 'Sobre Nosotros',
        about_who_we_are_title: '1. Quiénes Somos',
        about_who_we_are_text: 'Wafiyo es una moderna plataforma de fidelización digital diseñada para ayudar a los negocios locales a conectar con sus clientes. Creemos que construir la lealtad del cliente no debería requerir software costoso ni integraciones complejas. Nuestra misión es hacer que los programas de fidelización premium sean accesibles para empresas de todos los tamaños.',
        about_mission_title: '2. Nuestra Misión',
        about_mission_text: 'Nuestro objetivo es reemplazar las tarjetas de sellos de papel obsoletas por una experiencia digital fluida y ecológica. Al proporcionar a los negocios herramientas fáciles de usar e información procesable, les ayudamos a aumentar la retención de clientes y hacer crecer sus ingresos de forma orgánica.',
        about_why_choose_us_title: '3. ¿Por qué Elegirnos?',
        about_why_choose_us_text: 'Nos enfocamos en la simplicidad y la efectividad. Con Wafiyo, no hay ninguna app que tus clientes deban descargar, solo un simple escaneo QR para ganar puntos y reclamar recompensas. Para los dueños de negocios, nuestro panel intuitivo hace que el seguimiento del éxito sea fácil.',
        about_contact_title: '4. Ponte en Contacto',
        about_contact_text: 'Siempre estamos buscando mejorar y nos encanta escuchar a nuestros usuarios. Si tienes algún comentario o quieres saber más sobre cómo Wafiyo puede ayudar a tu negocio, no dudes en comunicarte a support@wafiyo.com.',

        // Contact
        contact_title: 'Contáctanos',
        contact_message: 'Para cualquier pregunta o problema, puedes contactarnos por estos canales:',
        contact_via_email: 'Contáctanos por Email',
        email_copied: '¡Correo copiado al portapapeles!',
        contact_message: 'Para cualquier pregunta o problema, puedes contactarnos en este email:',
        copy_email: 'Copiar',
        follow_us_instagram: 'Contáctanos en Instagram',
        // FAQ
        faq_title: 'Preguntas Frecuentes',
        faq_q1: '¿Es Wafiyo realmente gratis?',
        faq_a1: 'Ofrecemos una prueba gratuita de 30 días con nuestro plan mensual para que puedas experimentar todas nuestras funciones profesionales. Eliges tu plan antes de comenzar y no se te cobrará hasta que termine el período de prueba. Puedes cancelar en cualquier momento — sin permanencia, sin preguntas.',
        faq_q2: '¿Mis clientes necesitan descargar una aplicación?',
        faq_a2: '¡No es necesario descargar ninguna aplicación! Los clientes simplemente escanean su código QR y la tarjeta se abre en su navegador móvil. También pueden guardarla en Google Wallet o Apple Wallet para un acceso fácil.',
        faq_q3: '¿Están mis datos seguros?',
        faq_a3: 'Sí, nos tomamos la seguridad muy en serio. Todos los datos están cifrados y se almacenan utilizando medidas de seguridad estándar de la industria. Además, mantienes el 100% de la propiedad de los datos de tus clientes.',
        faq_q4: '¿Cómo recompenso a mis clientes?',
        faq_a4: 'Cuando un cliente te visite, simplemente escanea el código QR de su tarjeta digital con tu teléfono. Nuestro sistema rastrea sus visitas automáticamente y te avisa cuando le corresponde una recompensa.',
        faq_q5: '¿Puedo personalizar mi tarjeta digital?',
        faq_a5: '¡Absolutamente! Nuestros planes profesionales te permiten personalizar los colores de la tarjeta, añadir tu propio logo y establecer tus propios límites de recompensa para que coincidan con tu marca.',
        // Past Due
        past_due_title: 'Pago Fallido',
        past_due_desc: 'No pudimos procesar el pago de tu suscripción más reciente. Para evitar la interrupción del servicio y recuperar el acceso a tu panel, actualiza tu método de pago.',
        past_due_btn: 'Actualizar Método de Pago',
        past_due_portal_err: 'No se pudo abrir el portal de facturación. Contacta con soporte.',

        // Onboarding Checklist
        onboarding_title: 'Guía de Inicio Rápido',
        onboarding_subtitle: 'Completa estos 4 sencillos pasos para activar tu tarjeta de fidelización y empezar a recibir clientes.',
        onboarding_completed_title: '¡Configuración Lista! 🎉',
        onboarding_completed_desc: 'Tu tarjeta de fidelización está lista y activa para tus clientes.',
        onboarding_dismiss_hint: 'Puedes volver a abrir esta guía en cualquier momento desde Ajustes.',
        onboarding_need_client_first: '¡Primero añade o abre tu tarjeta de prueba en el Paso 2!',
        onboarding_all_done_headline: '¡Todo listo al 100% para tu negocio!',
        onboarding_all_done_sub: 'Tu programa de fidelización está activo. Coloca tu cartel QR en el mostrador y premia a tus clientes.',
        onboarding_dismiss_btn: 'Ocultar Guía y Empezar',
        onboarding_reopen_btn: 'Guía de Inicio Rápido',
        onboarding_reopened: '¡Guía de inicio rápido reabierta en tu panel!',
        onboarding_step1_title: 'Diseña tu Tarjeta de Fidelización',
        onboarding_step1_desc: 'Elige los colores de tu marca, el icono de sello y define tu meta de recompensas.',
        onboarding_step1_btn: 'Personalizar Tarjeta',
        onboarding_step2_title: 'Añade la Tarjeta de Prueba a tu Móvil',
        onboarding_step2_desc: 'Mira cómo se ve en Apple o Google Wallet. Ábrela en tu móvil o escanéala con tu cámara.',
        onboarding_step2_btn: 'Obtener Tarjeta de Prueba',
        onboarding_step3_title: 'Prueba a Sellar una Visita',
        onboarding_step3_desc: 'Experimenta la actualización en tiempo real. Sella a tu cliente de prueba en 1 clic o usa el escáner.',
        onboarding_step3_stamp_btn: '⚡ +1 Sello de Prueba',
        onboarding_step3_scan_btn: 'Abrir Escáner',
        onboarding_step4_title: 'Descarga tu Cartel QR de Mostrador',
        onboarding_step4_desc: 'Imprime tu soporte QR listo para usar o muéstralo en tu caja registradora.',
        onboarding_step4_btn: 'Descargar Cartel de Mostrador',
        onboarding_got_it: '¡Entendido!',
        ready_to_print: 'Listo para imprimir',
        downloaded: 'Descargado',
        completed: 'completado',
        collapse: 'Minimizar',
        expand: 'Expandir',
        dismiss: 'Cerrar',
        view_qr: 'Ver QR',
        qr_modal_tip_title: 'Cómo probar:',
        qr_modal_tip_desc: 'Si estás en el móvil, pulsa "Abrir en este móvil". Si estás en ordenador, escanea el código con tu cámara.',
        open_on_device: 'Abrir en este móvil',
        copy_link: 'Copiar Enlace',
        link_copied: '¡Enlace de registro copiado al portapapeles!',
        download_poster: 'Descargar Cartel de Mostrador (Imprimible)',
        onboarding_cartel_what_title: '¿Qué es esto?',
        onboarding_cartel_what_desc: 'Un cartel/soporte QR listo para imprimir para tu mostrador.',
        onboarding_cartel_how_title: '¿Cómo se usa?',
        onboarding_cartel_how_desc: 'Imprímelo y colócalo donde cobran tus clientes. Ellos lo escanean al pagar para unirse en 3 segundos sin descargar apps.',
        onboarding_client_preview_title: 'Vista de Tarjeta del Cliente',
        onboarding_client_preview_desc: 'Esta es la misma tarjeta y botones de Wallet que reciben tus clientes:'
    },
    fr: {
        // General
        cancel: 'Annuler',
        close: 'Fermer',
        loading: 'Chargement…',
        search: 'Recherche par nom ou email…',
        business_not_found: 'Entreprise non trouvée.',
        client_label: 'Client',
        visits_label: 'Visites',
        stamp_n: 'Tampon {{count}}',
        err_biz_loading: 'Attendez un moment... Récupération de vos détails en cours.',
        hello_prefix: 'Bonjour',
        now: 'Maintenant',
        preview: 'Aperçu',
        apple: 'Apple',
        google: 'Google',
        name: 'Nom',
        until_reward: 'JUSQU\'À LA RÉCOMPENSE',
        stamps_suffix: 'tampons',
        ready: 'Prêt !',
        email: 'E-mail',
        logo: 'Logo',
        design: 'Design',

        // Navbar
        my_qr_code: '🏪 Mon code QR',
        scan_qr: '📷 Scanner QR',
        add_client: '➕ Ajouter un client',
        sign_out: 'Déconnexion',
        dashboard: 'Tableau de bord',
        back: 'Retour',
        card_design: 'Design de la carte',

        // Landing
        landing_subtitle: 'Le magnifique système de carte de fidélité numérique. Fidélisez vos clients sans les cartes papier.',
        get_started_free: 'Commencer gratuitement',
        see_how_it_works: 'Voir comment ça marche',
        how_it_works: 'Comment ça marche',
        step_1_title: '1. Inscrivez votre entreprise',
        step_1_desc: "Créez un compte en quelques secondes. Vous recevrez un code QR unique pour le comptoir de votre boutique.",
        step_2_title: '2. Les clients scannent pour rejoindre',
        step_2_desc: 'Aucune application requise. Les clients scannent votre QR pour enregistrer instantanément leur carte numérique.',
        step_3_title: '3. Scannez pour récompenser',
        step_3_desc: 'Lors de leur visite, scanné leur téléphone pour ajouter un tampon. 10 tampons = récompense gratuite !',
        features_title: 'Pourquoi choisir notre plateforme ?',
        feat_1_title: 'Aucune application requise',
        feat_1_desc: 'Fonctionne entièrement dans le navigateur. Les clients scannent et enregistrent simplement sur Google Wallet ou Apple Wallet.',
        feat_2_title: 'Installation instantanée',
        feat_2_desc: 'Soyez opérationnel en quelques minutes. Aucun matériel complexe ou intégration POS requis.',
        feat_3_title: 'Données et analyses',
        feat_3_desc: 'Sachez exactement qui sont vos meilleurs clients et à quelle fréquence ils visitent votre entreprise.',
        ready_to_start: 'Prêt à moderniser votre programme de fidélité ?',
        create_business_account: 'Créer un compte entreprise',
        go_to_dashboard: 'Aller au tableau de bord',
        landing_badge: '✨ La carte de tampon numérique #1',
        hero_title_1: 'La fidélité à l\'ancienne,',
        hero_title_2: 'réimaginée pour l\'ère numérique.',
        customer_rating: 'Évaluation des clients',
        apps_to_download: 'Applications à télécharger',
        data_ownership: 'Propriété des données',
        how_it_works_subtitle: 'Simple à utiliser. Facile à configurer. Gérer votre programme de fidélité n\'a jamais été aussi simple.',
        footer_tagline: 'Donner aux entreprises les moyens d\'agir avec des solutions de fidélisation numériques modernes.',
        product: 'Produit',
        features: 'Fonctionnalités',
        company: 'Entreprise',
        about_us: 'À propos de nous',
        contact: 'Contact',
        all_rights_reserved: 'Tous droits réservés.',

        // Redesign Sections
        announcement_text: '🏆 La carte de tampon numérique #1 — Pas d\'applications. Pas de papier. Juste des résultats.',
        promo_banner: '🎁 Profitez d\'un mois d\'essai gratuit ! L\'offre se termine le 1er décembre',
        see_more: 'Voir plus →',
        pill_badge: '✦ Cartes numériques pour votre commerce',
        hero_h1: 'Chaque client qui ne revient pas, c\'est de l\'argent perdu.',
        hero_sub: 'Créez des cartes de tampons numériques en quelques minutes. Vos clients les enregistrent sur Apple ou Google Wallet — sans télécharger d\'application.',
        cta_free: 'Créer votre compte gratuit',
        cta_demo: 'Voir les plans →',
        demo_banner_title: 'Mode Démo : Wafiyo Café',
        demo_banner_text: 'Explorez le tableau de bord avec des données fictives. Les modifications ne seront pas enregistrées.',
        install_banner_title: 'Installer Wafiyo',
        install_banner_android: 'Ajoutez Wafiyo à votre écran d\'accueil pour un accès rapide.',
        install_banner_ios: 'Appuyez sur le bouton Partager, puis "Sur l\'écran d\'accueil".',
        install_banner_btn: 'Installer',
        cta_real_signup: 'Commencer l\'essai réel →',
        demo_feature_restricted: 'L\'intégration du Wallet est désactivée en mode démo.',
        demo_sidebar_cta: 'Prêt à lancer votre propre programme de fidélité ?',
        trust_1: 'Gratuit pour commencer',
        trust_2: '30 jours d\'essai gratuit',
        trust_3: 'Configuration en 5 min',
        social_proof: 'Approuvé par des entreprises à travers le monde',
        problem_h2: 'Les cartes papier sont dépassées',
        pain_1_title: 'Perdues ou oubliées à la maison',
        pain_1_desc: 'Les clients les oublient ou les perdent, rompant ainsi facilement la boucle de fidélité.',
        pain_2_title: "Vous ne connaissez pas vos meilleurs clients",
        pain_2_desc: "Il est impossible de suivre qui visite le plus sans outils numériques.",
        pain_3_title: "Les clients ne reviennent pas après la première visite",
        pain_3_desc: 'Sans raison de revenir, les clients occasionnels disparaissent.',
        transition_text: 'Wafiyo résout tout cela — en moins de 5 minutes.',
        how_it_works_h2: 'Comment ça marche',
        step_1_title: '1. Enregistrez votre entreprise',
        step_1_desc: 'Créez votre compte et obtenez votre code QR unique.',
        step_2_title: '2. Vos clients scannent',
        step_2_desc: 'Ils rejoignent instantanément depuis leur navigateur.',
        step_3_title: '3. Scannez et récompensez',
        step_3_desc: 'Vous scannez leur carte à chaque visite.',
        feat_1_title_new: 'Aucune application',
        feat_1_desc_new: 'Vos clients n\'ont besoin que de leur téléphone. Ils scannent, s\'inscrivent et enregistrent leur carte dans Wallet en quelques secondes.',
        feat_2_title_new: 'Concevez votre carte',
        feat_2_desc_new: 'Personnalisez les couleurs, le logo et les icônes de tampons pour refléter votre marque.',
        feat_3_title_new: 'Tableau de bord analytique',
        feat_3_desc_new: 'Un tableau de bord complet avec des graphiques en barres, en aires et en secteurs. Suivez les visites par jour, heure et mois. Consultez votre taux de rétention et vos indicateurs de conversion en un coup d\'œil.',
        feat_4_title_new: 'Insights intelligents',
        feat_4_desc_new: 'Connaissez votre meilleur jour de la semaine, vos heures de pointe et quels clients sont à un tampon de leur récompense. Des décisions basées sur les données en toute simplicité.',
        metric_1_val: '40%',
        metric_1_label: 'Taux de conversion moyen',
        metric_2_val: '0 appli',
        metric_2_label: 'Téléchargée par vos clients',
        metric_3_val: '5 min',
        metric_3_label: 'Pour configurer votre programme',
        testi_h2: 'Ce que disent nos entreprises',
        pricing_toggle_mo: 'Mensuel',
        pricing_toggle_yr: 'Annuel',
        cancel_anytime: '✓ Essai gratuit inclus. Sans engagement. Annulez à tout moment.',
        cta_monthly: 'Commencez votre essai gratuit',
        cta_yearly: 'Économisez avec le plan annuel',
        // Analytics page
        analytics_visits_this_week: 'Visites cette semaine',
        analytics_retention_rate: 'Taux de rétention',
        analytics_stamps_given: 'Tampons donnés',
        analytics_conversion_rate: 'Taux de conversion',
        analytics_visits_per_day: 'Visites par jour',
        analytics_tab_week: 'Semaine',
        analytics_tab_month: 'Mois',
        analytics_tab_year: 'Année',
        analytics_visits_per_hour: 'Visites par heure',
        analytics_client_retention: 'Rétention client',
        analytics_returning: 'Revenus',
        analytics_new: 'Nouveaux',
        analytics_top_clients: 'Clients les plus actifs',
        analytics_visits: 'visites',
        analytics_stamps: 'tampons',
        analytics_no_clients: 'Pas encore de clients',
        analytics_best_day: 'Meilleur jour',
        analytics_peak_hour: 'Heure de pointe',
        analytics_upcoming_rewards: 'Récompenses à venir',
        analytics_total_visits: 'visites totales',
        analytics_clients: 'clients',
        analytics_one_stamp_away: 'À 1 tampon de la récompense',
        last_visit: 'Dernière visite',
        no_visits_yet: 'Pas encore de visites',
        time_ago: 'il y a',
        time_just_now: 'À l\'instant',
        notif_title: 'Envoyer une notification',
        notif_placeholder: 'Tapez votre message...',
        notif_send: 'Envoyer à',
        notif_clients_suffix: 'clients',
        notif_sending: 'Envoi en cours...',
        notif_cooldown: 'Prochaine notification disponible dans',
        notif_char_limit: 'caractères',
        notif_last_sent: 'Dernier envoi',
        notif_success: 'Notification envoyée avec succès !',
        notif_empty: 'Veuillez saisir un message',
        notif_no_clients: 'Aucun client à notifier',
        notif_remaining: 'restantes aujourd\'hui',
        cookie_text: 'Nous utilisons des cookies pour améliorer votre expérience. Vous pouvez accepter ou refuser les cookies non essentiels.',
        cookie_policy_link: 'Politique de confidentialité',
        cookie_accept: 'Accepter',
        cookie_decline: 'Refuser',
        analytics_title: 'Analyses',
        view_all: 'Voir tout',
        sp_cafes: 'Cafés',
        sp_barbers: 'Barbiers',
        sp_restaurants: 'Restaurants',
        sp_shops: 'Boutiques',
        mock_best_day: 'Meilleur jour',
        mock_saturday: 'Samedi',
        mock_peak_hour: 'Heure de pointe',
        mock_upcoming_rewards: 'Récompenses à venir',
        mock_clients_count: '3 clients',
        feat_5_title_new: 'Notifications push via Wallet',
        feat_5_desc_new: 'Envoyez des messages personnalisés directement sur les téléphones de vos clients. Votre message apparaît sur leur écran de verrouillage via Apple Wallet et Google Wallet — aucune application requise.',
        feat_5_mock_msg: 'Happy hour aujourd\'hui ! 🔥',
        feat_5_mock_sent: 'Envoyé à 47 clients ✓',
        feat_5_mock_lock: 'Apparaît sur l\'écran de verrouillage',
        hero_push_bold: 'Plus qu\'une visite !',
        hero_push_body: 'Viens cette semaine et obtiens ton prochain café gratuit chez Tormen Cafetería.',
        hero_push_meta: 'Notification push · il y a 2 heures',
        hero_social_proof: '+200 commerces',
        hero_social_proof_suffix: 'utilisent déjà Wafiyo dans le monde',
        push_notifications_feature: 'Notifications push',
        final_cta_h2: 'Prêt à moderniser votre programme de fidélité ?',
        final_cta_sub: 'Rejoignez les entreprises qui renforcent déjà la fidélité sans papier.',
        final_cta_btn: 'Commencer gratuitement maintenant',
        final_cta_micro: '30 jours d\'essai gratuit · Installation en 5 minutes',

        // Login
        back_to_home: 'Retour à l\'accueil',
        loyalty_card_manager: 'Wafiyo',
        sign_in_business: 'Connectez-vous à votre compte entreprise',
        create_biz_account: 'Créez votre compte entreprise',
        business_name: 'Nom de l\'entreprise',
        email: 'E-mail',
        password: 'Mot de passe',
        please_wait: 'Veuillez patienter…',
        sign_in: 'Se connecter',
        create_account: 'Créer un compte',
        dont_have_account: 'Vous n\'avez pas de compte ? ',
        already_have_account: 'Vous avez déjà un compte ? ',
        sign_up_link: 'S\'inscrire',
        forgot_password_link: 'Mot de passe oublié ?',
        forgot_pw_title: 'Réinitialiser le mot de passe',
        forgot_pw_subtitle: 'Entrez votre e-mail et nous vous enverrons un lien',
        forgot_pw_success: 'Vérifiez votre e-mail, nous vous avons envoyé un lien pour réinitialiser votre mot de passe',
        send_link: 'Envoyer le lien',
        err_email_not_found: 'Nous n\'avons pas trouvé de compte avec cet e-mail',
        reset_pw_title: 'Nouveau mot de passe',
        reset_pw_subtitle: 'Entrez votre nouveau mot de passe',
        reset_pw_label: 'Nouveau mot de passe',
        reset_pw_confirm: 'Confirmer le mot de passe',
        reset_pw_update_btn: 'Mettre à jour le mot de passe',
        err_pw_match: 'Les mots de passe ne correspondent pas',
        err_pw_length: 'Le mot de passe doit comporter au moins 8 caractères',
        reset_pw_success: 'Mot de passe mis à jour avec succès',
        redirecting_login: 'Redirection vers la connexion...',
        verifying: 'Vérification...',
        account_confirmed: 'Compte confirmé !',
        account_confirmed_msg: 'Votre compte a été vérifié avec succès',
        invalid_link: 'Lien invalide',
        invalid_link_msg: 'Ce lien a expiré ou a déjà été utilisé. Essayez de vous réinscrire ou contactez le support.',
        go_to_dashboard: 'Aller au tableau de bord',
        back_to_login: '← Retour à la connexion',
        check_email_in: 'Vérifiez votre e-mail à',
        check_email_msg: 'Nous vous avons envoyé un lien de confirmation pour activer votre compte.',
        check_spam: 'Vous ne le trouvez pas ? Vérifiez votre dossier de courrier indésirable',
        i_confirmed_already: 'J\'ai déjà confirmé mon e-mail',
        err_confirm_email_first: 'Confirmez votre e-mail avant de continuer',
        back_to_register: 'Retour à l\'inscription',

        // Pricing
        choose_your_plan: 'Choisissez votre plan',
        pricing_subtitle: 'Libérez tout le potentiel de votre entreprise avec notre système de fidélité professionnel.',
        monthly_plan: 'Plan mensuel',
        yearly_plan: 'Plan annuel',
        month: 'mois',
        first_month: 'premier mois',
        then_price: 'Ensuite {{symbol}}{{amount}}/mois',
        year: 'an',
        days_free_trial: 'jours d\'essai gratuit',
        subscribe_now: 'S\'abonner maintenant',
        choose_monthly: 'Choisir le plan mensuel',
        migration_pricing_title: 'Mise à jour du système de paiement',
        migration_pricing_desc: 'Nous avons changé de prestataire de paiement. Veuillez sélectionner un plan ci-dessous pour continuer à utiliser Wafiyo. Votre essai gratuit a déjà été utilisé, la facturation commencera donc immédiatement.',
        subscribe_now: 'S\'abonner maintenant',
        choose_monthly: 'Choisir le plan mensuel',
        choose_yearly: 'Choisir le plan annuel',
        migration_alert_expired_title: '⚠️ Action requise',
        migration_alert_update_title: '💳 Mettez à jour votre moyen de paiement',
        migration_alert_expired_desc: 'Votre abonnement est terminé. Réactivez votre plan {{plan}} pour continuer à gérer vos cartes de fidélité et vos clients.',
        migration_alert_update_desc_date: 'Nous avons changé de prestataire de paiement. Votre plan {{plan}} est actif jusqu\'au {{date}} — ajoutez simplement votre carte maintenant et vous ne serez pas facturé avant cette date. Cela ne prend qu\'une minute !',
        migration_alert_update_desc_nodate: 'Nous avons changé de prestataire de paiement. Veuillez ajouter vos informations de paiement pour conserver votre plan {{plan}} actif.',
        migration_btn_reactivate: 'Réactiver le plan {{plan}}',
        migration_btn_secure: 'Sécuriser mon plan {{plan}}',
        activating: 'Activation en cours...',

        // ROI Calculator
        roi_section_title: 'Découvrez l\'impact sur votre entreprise',
        roi_title: 'Calculez votre ROI',
        roi_desc: 'Voyez combien de revenus vous perdez en ne fidélisant pas vos clients.',
        roi_daily_customers: 'Clients Quotidiens',
        roi_avg_spend: 'Ticket Moyen',
        roi_extra_revenue: 'Revenus Supplémentaires Potentiels',
        roi_per_month: 'par mois',
        roi_disclaimer: 'Basé sur une augmentation conservatrice de 12% des revenus grâce à une meilleure rétention.',
        roi_cta: 'Commencez à Récupérer vos Revenus',

        unlimited_clients: 'Clients illimités',
        custom_card_designs: 'Designs de carte personnalisés',
        qr_code_scanning: 'Scannage de code QR',
        testi_1_quote: "Wafiyo a simplifié mon programme de fidélité. Mes clients adorent ne pas avoir à garder un autre morceau de papier dans leur portefeuille.",
        testi_1_type: "Salon de beauté",
        testi_2_quote: "L'intégration avec Apple et Google Wallet est ce qui nous distingue. C'est moderne, rapide et professionnel.",
        testi_2_type: "Café",
        testi_3_quote: "Depuis que nous avons éliminé les cartes physiques, mon taux de retour client a augmenté de 20 %. Incroyable.",
        testi_3_type: "Restaurant",
        google_wallet_integration: 'Intégration Google & Apple Wallet',
        all_monthly_features: 'Toutes les fonctionnalités mensuelles',
        three_months_free: 'Comprend 3 mois gratuits',
        priority_support: 'Support prioritaire',
        best_value: 'Meilleure offre',
        save_amount: 'Économisez {{symbol}}{{amount}} par an !',
        start_free_trial: 'Commencer l\'essai gratuit de 30 jours',
        free_trial_badge: '1 mois d\'essai gratuit',
        choose_yearly: 'Choisir le plan annuel',
        pricing_footer_text: 'Tous les plans incluent des tampons illimités, l\'historique des scans et des analyses de base.',

        // Dashboard
        total_clients: 'Total clients',
        total_visits: 'Total visites',
        rewards_due: 'Récompenses dues',
        clients: 'Clients',
        no_clients_yet: 'Pas encore de clients — cliquez sur "Ajouter un client" pour commencer !',
        no_clients_match: 'Aucun client ne correspond à votre recherche.',
        loading_clients: 'Chargement des clients…',
        visits_today: 'Total visites',
        rewards: 'Récompenses',
        conversion: 'Conversion',
        conversion_tooltip: 'Calcul : (Total tampons / Total visites) * 100. Cela montre combien de visiteurs se convertissent en tampons actifs.',
        clients_tooltip: 'Nombre total de cartes de fidélité distribuées à vos clients.',
        visits_tooltip: 'Nombre total de tampons ou de visites enregistrés pour tous les clients.',
        rewards_tooltip: 'Nombre de clients ayant accumulé suffisamment de tampons et prêts à réclamer leur récompense gratuite.',
        recent_activity: 'Activité récente',
        no_recent_activity: 'Aucune activité récente',
        no_clients_found: 'Aucun client trouvé',
        settings: 'Paramètres',
        business_account: 'Compte entreprise',
        user_email: 'E-mail utilisateur',
        subscription: 'Abonnement',
        plan_status: 'Statut du plan',
        active: 'Actif',
        inactive: 'Inactif',
        manage_subscription: 'Gérer l\'abonnement',
        activity_visit: 'Visite enregistrée',
        activity_reg: 'Nouveau client rejoint',

        // Client Card
        progress_toward_reward: 'Progrès vers la récompense gratuite',
        visits: 'visites',
        reward_due: '🎉 Récompense due',
        record_visit: 'Enregistrer une visite',
        gift: '🎁 Cadeau',
        remove_visit: 'Supprimer',
        remove_visit_confirm: 'Êtes-vous sûr de vouloir supprimer 1 visite de',
        hide_qr: 'Masquer le QR',
        show_qr: '🔲 Afficher le QR',
        wallet_link: '📱 Lien Wallet',
        client_scans_this: 'Le client scanne ceci pour afficher sa carte',
        no_contact_info: 'Pas d\'infos de contact',

        // Add Client Modal
        add_new_client: '➕ Ajouter un nouveau client',
        card_created: '✅ Carte créée',
        client_name: 'Nom du client *',
        optional: '(Facultatif)',
        phone: 'Téléphone',
        visits_for_free_reward: 'Visites pour récompense gratuite',
        create_card: 'Créer la carte',
        creating: 'Création en cours…',
        share_with: 'Partagez ce code QR ou ce lien Google Wallet avec',
        client_registration_qr: 'QR d\'inscription client',
        add_to_wallet: 'Ajouter à Google Wallet',
        add_to_apple_wallet: 'Ajouter à Apple Wallet',
        wallet_not_configured: 'Google Wallet n\'est pas configuré — configurez les informations d\'identification du compte de service pour activer les liens Wallet.',

        // Card Customizer
        customize_card: 'Personnalisez votre carte',
        card_title: 'Titre de la carte / Nom de l\'entreprise',
        logo_upload: 'Logo de l\'entreprise',
        logo_change: 'Changer le logo',
        logo_remove: 'Supprimer',
        logo_upload_hint: 'PNG, JPG ou WEBP — max 2 Mo',
        err_logo_too_large: 'Le fichier ne peut pas dépasser 2 Mo',
        err_logo_wrong_type: 'Seules les images PNG, JPG ou WEBP sont autorisées',
        err_logo_upload_failed: 'Erreur lors du téléchargement du logo, veuillez réessayer',
        color_preset: 'Préréglages de marque',
        background_color: 'Couleur de la carte',
        text_color: 'Couleur du texte',
        number_color: 'Couleur des chiffres',
        stamp_circle_color: 'Couleur du tampon',
        stamp_icon: 'Icône de tampon',
        stamp_goal: 'Nombre de tampons pour la récompense',
        preview_stamps: 'Aperçu du nombre de visites',
        reward_text: 'Récompense (ex. Coupe de cheveux gratuite)',
        save_design: '💾 Publier le design',
        saving: 'Publication…',
        live_preview: 'Aperçu en direct',
        view_png: 'Voir en PNG →',
        wallet_preview: 'Aperçu du portefeuille mobile',
        wallet_header: 'Choisissez l\'en-tête de votre portefeuille',
        design_saved_notify: 'Succès ! Le nouveau design de votre carte de tampon est maintenant en ligne.',

        // Business QR Modal
        business_signup_qr: '🏪 QR d\'inscription entreprise',
        print_or_display: 'Imprimez ou affichez ce QR dans votre boutique. Les clients le scannent pour créer instantanément leur propre carte de fidélité — aucune application requise.',
        generating_qr: 'Génération du code QR…',
        download: '⬇️ Télécharger',
        print: '🖨️ Imprimer',
        print_poster_badge: 'Terminal de fidélité',
        print_poster_subtitle: 'Scannez pour rejoindre notre programme de fidélité numérique et commencez à gagner des récompenses dès aujourd\'hui.',
        print_poster_step_1: 'Scanner le QR',
        print_poster_step_2: 'S\'inscrire',
        print_poster_step_3: 'Ajouter au Wallet',
        print_poster_no_app: 'Pas de téléchargement d\'application nécessaire.',
        print_poster_powered_by: 'Propulsé par Wafiyo',

        // Client Register
        join_loyalty: 'Rejoignez Wafiyo — c\'est gratuit !',
        full_name: 'Nom complet',
        youre_in: 'Vous y êtes !',
        loyalty_card_ready: 'Votre carte de fidélité pour {{businessName}} est prête.',
        show_qr_every_visit: 'Présentez ce code QR à l\'équipe lors de chaque visite',
        your_goal: 'Votre objectif',
        collect_n_visits: 'Accumulez {{threshold}} visites pour gagner une récompense gratuite 🏆',
        welcome_aboard: 'Bienvenue à bord, {{clientName}} !',
        add_to_wallet_desc: 'Enregistrez votre carte de membre numérique sur votre téléphone pour un accès rapide.',

        // Notifications & Validation
        err_valid_name: 'Veuillez saisir un nom valide (2-50 caractères).',
        err_valid_email: 'Oups ! Cet e-mail ne semble pas correct. Veuillez le vérifier.',
        err_password_min: 'Le mot de passe doit comporter au moins 6 caractères.',
        err_password_weak: 'Presque ! Pour renforcer votre mot de passe, veuillez inclure au moins un chiffre.',
        err_valid_phone: 'Veuillez saisir un numéro de téléphone valide.',
        err_threshold_range: 'Le seuil de récompense doit être compris entre 1 et 100.',
        notify_reg_success: 'Inscription réussie ! Veuillez vérifier vos e-mails.',
        notify_welcome_back: 'Bienvenue de nouveau !',
        notify_welcome_ready: 'Bienvenue ! Votre carte est prête.',
        notify_visit_recorded: 'Visite enregistrée',
        notify_visit_removed: 'Visite supprimée',
        notify_reward_redeemed: '🎁 Récompense utilisée !',
        err_not_confirmed: 'E-mail non confirmé. Veuillez cliquer sur le lien dans votre boîte de réception.',
        confirm_remove_title: 'Supprimer la visite',
        confirm_action: 'Confirmer',

        // QR Scanner
        scan_title: '📷 Scanner le code QR du client',
        point_camera: 'Pointez la caméra vers le code QR d\'un client',
        err_invalid_qr: 'Code QR invalide — aucun clientId trouvé',
        err_camera_denied: 'Accès à la caméra refusé',
        done: 'Fait',
        reward_earned: '🎉 Récompense gratuite gagnée !',
        redeem_reward: 'Utiliser la récompense',

        // Legal
        legal: 'Mentions légales',
        and: 'et',
        accept_privacy_prefix: 'J\'accepte la',
        err_accept_privacy: 'Vous devez accepter la politique de confidentialité et les conditions d\'utilisation pour créer un compte.',
        legal_last_updated: 'Dernière mise à jour',

        // Privacy Policy
        privacy_policy_title: 'Politique de confidentialité',
        pp_intro_title: '1. Introduction',
        pp_intro_text: 'Bienvenue sur Wafiyo. Cette politique de confidentialité explique comment nous collectons, utilisons, stockons et protégeons vos informations personnelles lorsque vous utilisez notre plateforme de gestion de cartes de fidélité ("le Service"). En utilisant notre Service, vous consentez aux pratiques en matière de données décrites dans cette politique. Cette politique est conforme au Règlement général sur la protection des données (RGPD) de l\'UE et aux autres lois applicables sur la protection des données.',
        pp_data_collected_title: '2. Informations que nous collectons',
        pp_data_collected_text: 'Nous collectons les types d\'informations personnelles suivants pour fournir et améliorer nos services :',
        pp_data_item_name: 'Nom complet — pour identifier votre compte et personnaliser votre expérience.',
        pp_data_item_email: 'Adresse e-mail — pour la création de compte, la connexion et les communications liées au service.',
        pp_data_item_phone: 'Numéro de téléphone (facultatif) — pour un contact supplémentaire s\'il est fourni par l\'utilisateur.',
        pp_data_item_visits: 'Historique des visites — pour suivre la progression de la carte de fidélité et l\'éligibilité aux récompenses.',
        pp_data_item_payment: 'Informations de paiement — traitées en toute sécurité par notre prestataire de paiement Polar ; nous ne stockons pas les détails de carte bancaire.',
        pp_how_we_use_title: '3. Comment nous utilisons vos informations',
        pp_how_we_use_text: 'Nous utilisons vos données personnelles uniquement dans le but de fournir et d\'améliorer notre service de gestion de cartes de fidélité. Cela comprend la gestion des comptes utilisateurs, le suivi de l\'historique des visites, l\'émission de récompenses, le traitement des paiements et la communication des mises à jour importantes du service. Nous n\'utilisons pas vos données à des fins publicitaires ou marketing non liées à notre service. La base juridique du traitement est l\'exécution d\'un contrat (Article 6(1)(b) du RGPD) et votre consentement (Article 6(1)(a) du RGPD).',
        pp_data_protection_title: '4. Sécurité des données',
        pp_data_protection_text: 'Nous prenons la sécurité de vos données au sérieux. Toutes les données sont transmises via des connexions cryptées (HTTPS/TLS) et stockées à l\'aide de mesures de sécurité conformes aux normes de l\'industrie fournies par nos partenaires d\'infrastructure. L\'accès aux données personnelles est limité au seul personnel autorisé, et nous mettons en œuvre des politiques de sécurité au niveau des lignes au niveau de la base de données. En cas de violation de données, nous en informerons les utilisateurs concernés et les autorités compétentes dans les 72 heures, comme l\'exige le RGPD.',
        pp_third_parties_title: '5. Services tiers',
        pp_third_parties_text: 'Nous utilisons les services tiers suivants pour exploiter notre plateforme : (1) Supabase — pour l\'authentification, le stockage en base de données et les services API ; (2) Polar — pour le traitement des paiements et la gestion des abonnements ; (3) Resend — pour l\'envoi d\'e-mails transactionnels ; (4) Google — pour l\'intégration des cartes de fidélité Google Wallet. Chacun de ces services traite les données conformément à ses propres politiques de confidentialité et aux lois applicables sur la protection des données. Nous ne vendons, louons ou partageons ni vos informations personnelles avec d\'autres tiers à des fins commerciales.',
        pp_cookies_title: '6. Cookies et stockage local',
        pp_cookies_text: 'Nous utilisons des cookies essentiels et le stockage local pour maintenir votre session d\'authentification et mémoriser vos préférences linguistiques. Nous n\'utilisons pas de cookies de suivi, de cookies analytiques ou de cookies publicitaires tiers. Ces cookies essentiels sont strictement nécessaires au fonctionnement du Service et ne peuvent pas être désactivés.',
        pp_data_retention_title: '7. Rétention des données',
        pp_data_retention_text: 'Nous conservons vos données personnelles tant que votre compte est actif ou selon les besoins pour vous fournir nos services. Si vous supprimez votre compte, nous supprimerons vos données personnelles dans les 30 jours, sauf si nous sommes tenus de les conserver à des fins légales ou réglementaires. Les données relatives aux visites et aux cartes de fidélité associées à votre entreprise seront également supprimées lors de la suppression du compte.',
        pp_user_rights_title: '8. Vos droits (RGPD)',
        pp_user_rights_text: 'En vertu du RGPD, vous disposez des droits suivants : (a) Droit d\'accès — obtenir une copie de vos données personnelles ; (b) Droit de rectification — corriger des données personnelles inexactes ; (c) Droit à l\'effacement — demander la suppression de vos données personnelles ; (d) Droit à la limitation — limiter le traitement de vos données ; (e) Droit à la portabilité des données — recevoir vos données dans un format structuré et lisible par machine ; (f) Droit d\'opposition — s\'opposer au traitement de vos données personnelles ; (g) Droit d\'introduire une réclamation — déposer une plainte auprès d\'une autorité de contrôle de la protection des données. Pour exercer l\'un de ces droits, veuillez nous contacter à support@wafiyo.com. Nous répondrons à votre requête sous 30 jours.',
        pp_international_title: '9. Transferts internationaux de données',
        pp_international_text: 'Vos données peuvent être traitées dans des pays en dehors de l\'Espace économique européen (EEE) par nos prestataires de services tiers. Dans de tels cas, des garanties appropriées sont en place, y compris des clauses contractuelles types approuvées par la Commission européenne, pour garantir que vos données sont protégées conformément aux normes de protection des données de l\'UE.',
        pp_contact_title: '10. Contact',
        pp_contact_text: 'Si vous avez des questions, des préoccupations ou des demandes concernant cette politique de confidentialité ou nos pratiques en matière de données, veuillez nous contacter à support@wafiyo.com.',

        // Terms of Service
        tos_title: 'Conditions d\'utilisation',
        tos_acceptance_title: '1. Acceptation des conditions',
        tos_acceptance_text: 'En accédant ou en utilisant la plateforme Wafiyo ("le Service"), vous acceptez d\'être lié par ces conditions d\'utilisation. Si vous n\'acceptez pas toutes ces conditions, vous ne pouvez pas accéder ni utiliser notre Service. Ces conditions constituent un accord juridiquement contraignant entre vous ("l\'Utilisateur") et Wafiyo ("l\'Entreprise").',
        tos_service_title: '2. Description du Service',
        tos_service_text: 'Wafiyo fournit une plateforme numérique de gestion de cartes de fidélité qui permet aux propriétaires d\'entreprises de créer des programmes de fidélité, de suivre les visites des clients et de gérer les récompenses. Le Service comprend des fonctionnalités telles que le scannage de codes QR, la génération de cartes numériques (Google Wallet et Apple Wallet), des outils de relation client et des tableaux de bord analytiques. Les fonctionnalités peuvent être ajoutées, modifiées ou supprimées à notre discrétion.',
        tos_accounts_title: '3. Comptes utilisateurs',
        tos_accounts_text: 'Vous êtes responsable du maintien de la confidentialité de vos identifiants de compte et de toutes les activités qui se produisent sous votre compte. Vous acceptez de fournir des informations exactes et complètes lors de la création de votre compte et de maintenir ces informations à jour. Vous devez nous informer immédiatement de toute utilisation non autorisée de votre compte. Vous devez avoir au moins 18 ans ou avoir atteint l\'âge de la majorité légale dans votre juridiction pour utiliser ce Service.',
        tos_payment_title: '4. Paiements et abonnements',
        tos_payment_text: 'Certaines fonctionnalités du Service nécessitent un abonnement payant. Les paiements sont traités par notre prestataire de paiement autorisé, Polar.sh. En vous abonnant, vous autorisez Polar à débiter les frais applicables selon le mode de paiement choisi. Les abonnements se renouvellent automatiquement à la fin de chaque cycle de facturation, sauf s\'ils sont annulés avant la date de renouvellement. Les prix peuvent changer avec un préavis de 30 jours. Tous les prix sont affichés en euros (EUR) et incluent les taxes applicables selon votre localisation, déterminées par Polar.',
        tos_usage_title: '5. Utilisation acceptable',
        tos_usage_text: 'Vous acceptez d\'utiliser le Service uniquement à des fins licites et conformément à ces conditions. Vous ne devez pas : (a) utiliser la plateforme pour stocker ou transmettre tout contenu illégal, nuisible, menaçant, abusif ou autrement répréhensible ; (b) tenter d\'accéder sans autorisation au Service, à d\'autres comptes utilisateurs ou à des systèmes connexes ; (c) utiliser le Service pour envoyer du spam ou des communications non sollicitées ; (d) faire de l\'ingénierie inverse, décompiler ou désassembler toute partie du Service ; (e) utiliser des outils automatisés pour extraire des données du Service.',
        tos_ip_title: '6. Propriété intellectuelle',
        tos_ip_text: 'Le Service, y compris sa conception, son code, ses fonctionnalités et sa marque, est la propriété de Wafiyo et est protégé par les lois sur la propriété intellectuelle. Vous conservez la propriété de tout contenu ou donnée que vous téléchargez sur le Service. En utilisant le Service, vous nous accordez une licence limitée pour traiter et afficher votre contenu uniquement dans le but d\'exploiter le Service.',
        tos_termination_title: '7. Résiliation',
        tos_termination_text: 'Nous nous réservons le droit de suspendre ou de résilier votre accès au Service à tout moment en cas de violation de ces conditions, de non-paiement ou pour toute autre raison que nous jugeons nécessaire. En cas de résiliation, votre droit d\'utiliser le Service cessera immédiatement. Vous pouvez demander la suppression de votre compte à tout moment. Nous conserverons vos données pendant 30 jours après la résiliation pour permettre une éventuelle réactivation, après quoi elles seront définitivement supprimées.',
        tos_liability_title: '8. Limitation de responsabilité',
        tos_liability_text: 'Le Service est fourni "tel quel" et "selon disponibilité", sans garantie d\'aucune sorte, expresse ou implicite, y compris, mais sans s\'y limiter, les garanties de qualité marchande, d\'adéquation à un usage particulier et d\'absence de contrefaçon. Dans toute la mesure permise par la loi, Wafiyo ne sera pas responsable des dommages indirects, accessoires, spéciaux, consécutifs ou punitifs résultant de votre utilisation du Service ou s\'y rapportant. Notre responsabilité totale pour toute réclamation liée au Service ne dépassera pas le montant total payé par vous pour le Service au cours des douze (12) mois précédant la réclamation.',
        tos_governing_title: '9. Loi applicable et juridiction',
        tos_governing_text: 'Ces conditions d\'utilisation sont régies et interprétées conformément aux lois de l\'Espagne. Tout litige découlant de l\'utilisation du Service sera soumis à la juridiction exclusive des tribunaux d\'Espagne. Si une disposition de ces conditions est jugée inapplicable, les autres dispositions resteront pleinement en vigueur.',
        tos_changes_title: '10. Modifications des conditions',
        tos_changes_text: 'Nous nous réservons le droit de modifier ces conditions à tout moment. L\'utilisation continue du Service après toute modification constitue votre acceptation des nouvelles conditions. Nous informerons les utilisateurs des changements importants par e-mail ou via le Service au moins 30 jours avant leur entrée en vigueur.',
        tos_contact_title: '11. Contact',
        tos_contact_text: 'Si vous avez des questions concernant ces conditions d\'utilisation, veuillez nous contacter à support@wafiyo.com.',

        // Refund Policy
        refund_policy_title: 'Politique de remboursement',
        rp_intro_title: '1. Aperçu',
        rp_intro_text: 'Chez Wafiyo, nous nous efforçons de fournir la meilleure expérience de carte de fidélité numérique pour votre entreprise. Cette politique de remboursement décrit les conditions dans lesquelles des remboursements peuvent être accordés pour nos services. Tous les paiements sont traités par Polar.sh, notre marchand officiel autorisé.',
        rp_trial_title: '2. Essai gratuit',
        rp_trial_text: 'Nous offrons un essai gratuit de 30 jours pour les nouveaux abonnés. Pendant la période d\'essai, vous avez un accès complet à toutes les fonctionnalités sans frais. Vous ne serez facturé qu\'à la fin de la période d\'essai. Si vous annulez avant l\'expiration de l\'essai, vous ne serez pas facturé.',
        rp_subscriptions_title: '3. Abonnements payants',
        rp_subscriptions_text: 'Après la période d\'essai, votre abonnement sera automatiquement renouvelé au tarif applicable. Vous pouvez annuler votre abonnement à tout moment via le portail client. Les annulations prendront effet à la fin du cycle de facturation en cours, et vous conserverez l\'accès au Service jusqu\'à cette date. Nous ne fournissons pas de remboursements proratisés pour les périodes de facturation partielles.',
        rp_refund_window_title: '4. Fenêtre de remboursement',
        rp_refund_window_text: 'Si vous n\'êtes pas satisfait du Service, vous pouvez demander un remboursement complet dans les 14 jours suivant votre premier paiement (pas pendant l\'essai gratuit). Pour demander un remboursement, contactez-nous à support@wafiyo.com avec l\'adresse e-mail de votre compte et le motif de votre demande. Les remboursements après la fenêtre de 14 jours seront examinés au cas par cas.',
        rp_exceptions_title: '5. Interruptions de service',
        rp_exceptions_text: 'Si vous avez subi une interruption de service importante ou une erreur technique attribuable à notre plateforme qui vous a empêché d\'utiliser le Service pendant plus de 48 heures consécutives, vous pourriez avoir droit à un crédit proratisé ou à un remboursement pour la période concernée. Ces demandes doivent être soumises dans les 7 jours suivant l\'incident.',
        rp_how_title: '6. Traitement des remboursements',
        rp_how_text: 'Les remboursements approuvés seront traités par Polar et renvoyés vers le mode de paiement d\'origine. Les remboursements prennent généralement 5 à 10 jours ouvrés pour apparaître sur votre relevé, selon votre banque ou votre fournisseur de carte de crédit.',
        rp_contact_title: '7. Contactez-nous',
        rp_contact_text: 'Si vous pensez avoir droit à un remboursement ou si vous avez des questions sur cette politique, veuillez nous contacter à support@wafiyo.com.',

        // About Us
        about_title: 'À propos de nous',
        about_who_we_are_title: '1. Qui sommes-nous',
        about_who_we_are_text: 'Wafiyo est une plateforme de fidélisation numérique moderne conçue pour aider les entreprises locales à se connecter avec leurs clients. Nous pensons que fidéliser la clientèle ne devrait pas nécessiter de logiciels coûteux ou d\'intégrations complexes. Notre mission est de rendre les programmes de fidélité premium accessibles aux entreprises de toutes tailles.',
        about_mission_title: '2. Notre mission',
        about_mission_text: 'Nous visons à remplacer les cartes de tampons en papier obsolètes par une expérience numérique fluide et respectueuse de l\'environnement. En fournissant aux entreprises des outils faciles à utiliser et des informations exploitables, nous les aidons à accroître la rétention client et à développer leurs revenus de manière organique.',
        about_why_choose_us_title: '3. Pourquoi nous choisir ?',
        about_why_choose_us_text: 'Nous privilégions la simplicité et l\'efficacité. Avec Wafiyo, il n\'y a aucune application à télécharger pour vos clients — juste un simple scan QR pour gagner des points et réclamer des récompenses. Pour les propriétaires d\'entreprises, notre tableau de bord intuitif facilite le suivi du succès.',
        about_contact_title: '4. Contactez-nous',
        about_contact_text: 'Nous cherchons toujours à nous améliorer et nous aimons avoir des nouvelles de nos utilisateurs. Si vous avez des commentaires ou si vous voulez en savoir plus sur la façon dont Wafiyo peut aider votre entreprise, n\'hésitez pas à nous contacter à support@wafiyo.com.',

        // Contact
        contact_title: 'Contactez-nous',
        contact_message: 'Pour toute question ou problème, vous pouvez nous joindre via ces canaux :',
        contact_via_email: 'Contactez-nous par e-mail',
        email_copied: 'E-mail copié dans le presse-papiers !',
        copy_email: 'Copier',
        follow_us_instagram: 'Contactez-nous sur Instagram',

        // FAQ
        faq_title: 'Foire Aux Questions',
        faq_q1: 'Wafiyo est-il vraiment gratuit ?',
        faq_a1: 'Nous proposons un essai gratuit de 30 jours avec notre plan mensuel afin que vous puissiez découvrir toutes nos fonctionnalités professionnelles. Vous choisissez votre plan avant de commencer, et vous ne serez facturé qu\'à la fin de la période d\'essai. Vous pouvez annuler à tout moment — sans engagement, sans questions.',
        faq_q2: 'Mes clients doivent-ils télécharger une application ?',
        faq_a2: 'Aucun téléchargement d\'application n\'est requis ! Les clients scannent simplement votre code QR et la carte s\'ouvre dans leur navigateur mobile. Ils peuvent également l\'enregistrer dans Google Wallet ou Apple Wallet pour un accès facile.',
        faq_q3: 'Mes données sont-elles sécurisées ?',
        faq_a3: 'Oui, nous prenons la sécurité au sérieux. Toutes les données sont cryptées et stockées à l\'aide de mesures de sécurité conformes aux normes de l\'industrie. Vous conservez également la propriété à 100 % des données de vos clients.',
        faq_q4: 'Comment récompenser mes clients ?',
        faq_a4: 'Lorsqu\'un client vous rend visite, vous scannez simplement le code QR sur sa carte numérique à l\'aide de votre téléphone. Notre système suit automatiquement ses visites et vous informe lorsqu\'une récompense est due.',
        faq_q5: 'Puis-je personnaliser ma carte numérique ?',
        faq_a5: 'Absolument ! Nos plans professionnels vous permettent de personnaliser les couleurs de la carte, d\'ajouter votre propre logo et de définir vos propres seuils de récompense pour correspondre à votre image de marque.',

        // Past Due
        past_due_title: 'Échec du paiement',
        past_due_desc: 'Nous n\'avons pas pu traiter votre dernier paiement d\'abonnement. Pour éviter toute interruption de service et retrouver l\'accès à votre tableau de bord, veuillez mettre à jour votre mode de paiement.',
        past_due_btn: 'Mettre à jour le mode de paiement',
        past_due_portal_err: 'Impossible d\'ouvrir le portail de facturation. Veuillez contacter le support.',

        // Onboarding Checklist
        onboarding_title: 'Guide de Démarrage Rapide',
        onboarding_subtitle: 'Complétez ces 4 étapes simples pour lancer votre carte de fidélité et accueillir vos premiers clients.',
        onboarding_completed_title: 'Configuration Terminée ! 🎉',
        onboarding_completed_desc: 'Votre carte de fidélité est prête et active pour vos clients.',
        onboarding_dismiss_hint: 'Vous pouvez rouvrir ce guide à tout moment depuis les Paramètres.',
        onboarding_need_client_first: 'Ajoutez ou ouvrez d\'abord votre carte de test à l\'Étape 2 !',
        onboarding_all_done_headline: 'Tout est 100% prêt pour votre entreprise !',
        onboarding_all_done_sub: 'Votre programme de fidélité est en ligne. Placez votre présentoir QR au comptoir et récompensez vos clients.',
        onboarding_dismiss_btn: 'Masquer le Guide et Commencer',
        onboarding_reopen_btn: 'Guide de Démarrage Rapide',
        onboarding_reopened: 'Guide de démarrage rapide rouvert sur votre tableau de bord !',
        onboarding_step1_title: 'Personnalisez Votre Carte de Fidélité',
        onboarding_step1_desc: 'Choisissez les couleurs de votre marque, l\'icône de tampon et définissez votre objectif de récompense.',
        onboarding_step1_btn: 'Personnaliser la Carte',
        onboarding_step2_title: 'Ajoutez la Carte de Test sur Votre Téléphone',
        onboarding_step2_desc: 'Voyez à quoi ressemble votre carte dans Apple ou Google Wallet. Ouvrez-la sur votre téléphone ou scannez-la avec votre caméra.',
        onboarding_step2_btn: 'Obtenir la Carte de Test',
        onboarding_step3_title: 'Testez le Tamponnage d\'une Visite',
        onboarding_step3_desc: 'Découvrez la mise à jour instantanée. Tamponnez votre client de test en 1 clic ou utilisez le scanner de caméra.',
        onboarding_step3_stamp_btn: '⚡ +1 Tampon de Test',
        onboarding_step3_scan_btn: 'Ouvrir le Scanner',
        onboarding_step4_title: 'Téléchargez Votre Présentoir QR de Comptoir',
        onboarding_step4_desc: 'Imprimez votre présentoir QR prêt à l\'emploi ou affichez-le à votre caisse.',
        onboarding_step4_btn: 'Télécharger le Présentoir',
        onboarding_got_it: 'J’ai compris !',
        ready_to_print: 'Prêt à imprimer',
        downloaded: 'Téléchargé',
        completed: 'terminé',
        collapse: 'Réduire',
        expand: 'Développer',
        dismiss: 'Fermer',
        view_qr: 'Voir QR',
        qr_modal_tip_title: 'Comment tester :',
        qr_modal_tip_desc: 'Sur mobile, appuyez sur "Ouvrir sur ce téléphone". Sur ordinateur, scannez le QR code avec votre appareil photo.',
        open_on_device: 'Ouvrir sur ce téléphone',
        copy_link: 'Copier le Lien',
        link_copied: 'Lien d\'inscription copié dans le presse-papier !',
        download_poster: 'Télécharger le Présentoir de Comptoir (Imprimable)',
        onboarding_cartel_what_title: 'Qu\'est-ce que c\'est ?',
        onboarding_cartel_what_desc: 'Un présentoir QR prêt à imprimer pour votre comptoir ou caisse.',
        onboarding_cartel_how_title: 'Comment l\'utiliser :',
        onboarding_cartel_how_desc: 'Imprimez-le et placez-le à la caisse. Vos clients le scannent avec leur téléphone pour rejoindre en 3 secondes sans télécharger d\'application.',
        onboarding_client_preview_title: 'Aperçu Carte Client',
        onboarding_client_preview_desc: 'Ceci est la même carte et les boutons Wallet que vos clients reçoivent :'
    }
};

// ─── Context ─────────────────────────────────────────────────
const LanguageContext = createContext();

export function LanguageProvider({ children }) {
    const [lang, setLang] = useState(() => {
        // 1. Check localStorage for a saved preference
        const saved = localStorage.getItem('appLang');
        if (saved && ['en', 'es', 'fr'].includes(saved)) return saved;

        // 2. Detect browser/device language
        const browserLang = (navigator.language || navigator.userLanguage || '').toLowerCase();
        if (browserLang.startsWith('es')) return 'es';
        if (browserLang.startsWith('fr')) return 'fr';
        return 'en';
    });

    const changeLanguage = (newLang) => {
        setLang(newLang);
        localStorage.setItem('appLang', newLang);
    };

    const t = (key, interpolations = {}) => {
        let str = translations[lang][key] || translations['en'][key] || key;

        // Super lightweight interpolation logic {{var}}
        Object.keys(interpolations).forEach(k => {
            str = str.replace(`{{${k}}}`, interpolations[k]);
        });

        return str;
    };

    return (
        <LanguageContext.Provider value={{ lang, changeLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    );
}

export const useLanguage = () => useContext(LanguageContext);
